'use client'

/**
 * Product Card Glass Component
 * 
 * UI/UX ONLY - Renders glassmorphic product card
 * Does NOT modify recommendation logic or data
 */

import React, { useMemo, useEffect } from 'react'
import {
  Bed,
  Car,
  Circle,
  ExternalLink,
  HeartPulse,
  Link2,
  Shield,
  Tag,
  type LucideIcon,
} from 'lucide-react'
// Icons removed from hero cards - no longer needed
import { getHeroCardBackgroundStyle } from './bundleBackgrounds'
import { ReasoningBadge } from './ReasoningBadge'
import { 
  generateReasoningBadge, 
  calculateMobilityScore,
  isProductSafeForMobility,
  type PetTraits,
  type WeatherContext 
} from '@/lib/agenticReasoning/reasoningMatrix'
import { AIBadge } from './AIBadge'
import { AIFitBlock } from './AIFitBlock'
import { getEmojiForProduct } from './emojiMap'
import { getBundleCategoryUrl } from '@/lib/picks/bundleCategoryUrls'
import { 
  nuclearScrubSlug, 
  isSlugPattern, 
  isBundleName,
  BUNDLE_NAMES_TO_FILTER 
} from '@/lib/utils/slugScrubber'
import { resolveTier } from '@/lib/subscription/tierResolver'
import { trackEvent } from '@/lib/analytics'
import {
  isPicksV2Visual,
  picksV2CtaPrimary,
  picksV2GlassBlur,
  picksV2HeroFrameStyle,
  picksV2HeroRecommendationCardStyle,
  picksV2InfoPanelStyle,
  picksV2ProductCardStyle,
  picksV2Type,
  shouldHideProductsCategoryHeroPhoto,
  type PicksProductsTheme,
} from '@/lib/picks/picksV2Visual'
import { homeV2Glass, homeV2Text } from '@/lib/home/homeV2Theme'

const HERO_RECOMMENDATION_MAX_LEN = 90

function resolveHeroCategoryIcon(searchText: string): LucideIcon | null {
  const s = searchText.toLowerCase()
  if (/\bharness/.test(s)) return Shield
  if (/\bleash|\blead\b/.test(s)) return Link2
  if (/\bcollar/.test(s) && !/\btag/.test(s)) return Circle
  if (/\btag\b|id tag/.test(s)) return Tag
  if (/supplement|joint|vitamin|wellness|health|hip|mobility/.test(s)) return HeartPulse
  if (/travel|carrier|\bcar\b/.test(s)) return Car
  if (/bed|furniture|crate|sleep/.test(s)) return Bed
  return null
}

/** Single-sentence hero copy, capped for scanability. */
function formatHeroRecommendationSnippet(
  raw: string,
  maxLen = HERO_RECOMMENDATION_MAX_LEN
): string | null {
  const first = raw
    .split(/[.!?]+/)
    .map((sentence) => sentence.trim())
    .find((sentence) => sentence.length > 10)
  if (!first) return null
  if (first.length <= maxLen) {
    return first.endsWith('.') ? first : `${first}.`
  }
  const cut = first.slice(0, maxLen - 1).trimEnd()
  const lastSpace = cut.lastIndexOf(' ')
  const trimmed = lastSpace > 36 ? cut.slice(0, lastSpace) : cut
  return `${trimmed}…`
}

interface ProductCardGlassProps {
  product: {
    id: string
    title?: string
    name?: string
    brand?: string
    subcategory?: string
    category?: string
    reasoning?: string
    reason?: string
    aiReason?: string
    finalUrl?: string
    url?: string
    trackingUrl?: string
    affiliateUrl?: string
    matchScore?: number // NEW: Agentic match score (0-100)
    suitabilityReason?: string | null // NEW: Agentic suitability reason
    provider?: string // Affiliate partner/provider name (e.g., 'chewy', 'amazon')
    merchant?: string // Alternative field for affiliate partner
    /** Optional category-representative image (collection atmosphere, not SKU deep-link). */
    categoryImageUrl?: string
    imageUrl?: string
    image?: string
    /** When set to category_representative, imagery must not imply a specific SKU destination. */
    visualRole?: 'category_representative' | 'sku' | string
    /** Optional verified price (SKU cards). Omit when absent — never invent. */
    price?: number | string
    currency?: string
    /** Optional CTA label override (e.g. Explore harnesses. / View product). */
    ctaLabel?: string
    /** Separate subcategory browse link (hero keeps getBundleCategoryUrl). */
    subcategoryBrowseUrl?: string
    subcategoryBrowseCta?: string
    /** Subcategory label shown separately from catalog product title (SKU cards). */
    subcategoryLabel?: string
    /**
     * In-place SKU enrichment on an existing subcategory card (not a second row).
     * Does not change parent selection, scores, or hero destination logic.
     */
    skuEnriched?: boolean
    representativeCatalogName?: string
    representativeImageUrl?: string
    viewProductUrl?: string
    viewProductCta?: string
    enrichCatalogItemId?: string
    /** Preview-only SKU carousel attached to hero — rendered by BundleSection. */
    skuCarousel?: unknown[]
    catalogItemId?: string
  }
  bundleTitle: string
  theme: PicksProductsTheme
  isHero?: boolean
  bundleId?: string // NEW: Bundle ID for category-level URL lookup
  dogProfile?: {
    id?: string
    name?: string
    breed?: string
    ageYears?: number
    ageBracket?: string
    size?: string
  } | null
  weather?: {
    condition?: string
    temperature?: number
    humidity?: number
  } | null
  city?: string | null
  planTier?: 'FREE' | 'PACK_LEADER' | null // Legacy prop, kept for backward compatibility
  showPremiumPreview?: boolean // NEW: Show premium preview badge (FREE users, first card only)
  userProfile?: {
    subscriptionTier?: string | null
    planTier?: string | null
  } | null // NEW: User profile for unified tier resolution
  /** Effective Pack Leader tier from app shell (matches PackLeaderBadge). */
  isPackLeaderUser?: boolean
  /** Eager + high fetchPriority for first visible SKU card images (LCP). */
  prioritizeImage?: boolean
}


/** Category atmosphere well — decorative only; CTA stays on subcategory collection URL. */
function CategoryImageryWell({
  categoryRepUrl,
  fallbackEmoji,
  isV2,
  isHero,
  prioritizeImage = false,
}: {
  categoryRepUrl?: string | null
  fallbackEmoji: string | null
  isV2: boolean
  isHero: boolean
  /** First visible SKU cards — eager + high fetch priority for LCP. */
  prioritizeImage?: boolean
}) {
  const [failed, setFailed] = React.useState(false)
  const showImage = Boolean(categoryRepUrl) && !failed
  // One consistent surface (no cream frame over merchant white). Merchant BGs untouched.
  const wellStyle: React.CSSProperties = isHero
    ? {
        width: '100%',
        height: 176,
        borderRadius: 16,
        background: '#FFFFFF',
        border: isV2
          ? '1px solid rgba(255, 236, 210, 0.22)'
          : '1px solid rgba(255, 255, 255, 0.16)',
      }
    : {
        // Match hero surface; slightly larger for carousel readability; fixed size keeps icon-fallback cards aligned.
        width: 124,
        height: 124,
        borderRadius: 16,
        background: '#FFFFFF',
        border: isV2
          ? '1px solid rgba(255, 236, 210, 0.22)'
          : '1px solid rgba(255, 255, 255, 0.16)',
      }

  return (
    <div className={`flex ${isHero ? 'mb-2' : 'justify-center mb-1.5'}`}>
      <div
        className="flex items-center justify-center icon-container overflow-hidden"
        style={wellStyle}
        aria-hidden
      >
        {showImage ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={categoryRepUrl as string}
            alt=""
            data-category-representative="true"
            className="max-w-full max-h-full w-auto h-auto object-contain pointer-events-none select-none"
            style={{
              // Tighter padding → higher product occupancy; still fully visible (contain).
              padding: isHero ? 6 : 5,
              objectFit: 'contain',
            }}
            loading={prioritizeImage ? 'eager' : 'lazy'}
            fetchPriority={prioritizeImage ? 'high' : 'auto'}
            decoding={prioritizeImage ? 'sync' : 'async'}
            draggable={false}
            onError={() => setFailed(true)}
          />
        ) : (
          <span
            className="text-4xl"
            style={{ fontSize: isHero ? '3.25rem' : '2.85rem', lineHeight: 1 }}
          >
            {fallbackEmoji || '🐾'}
          </span>
        )}
      </div>
    </div>
  )
}

export function ProductCardGlass({ product, bundleTitle, theme, isHero = false, bundleId, dogProfile, weather, city, planTier = 'FREE', showPremiumPreview = false, userProfile, isPackLeaderUser = false, prioritizeImage = false }: ProductCardGlassProps) {
  // Effective tier: explicit flag from PicksTab/session wins over stale Free userProfile.
  const { tier } = resolveTier(userProfile)
  const isPackLeader =
    isPackLeaderUser === true ||
    tier === 'PACK_LEADER' ||
    tier === 'TOP_DOG' ||
    planTier === 'PACK_LEADER'
  const isV2 = isPicksV2Visual(theme)
  const hideCategoryHeroPhoto =
    isHero && shouldHideProductsCategoryHeroPhoto(theme)
  const showCategoryHeroPhoto = isHero && !hideCategoryHeroPhoto
  const usesLightHeroText = hideCategoryHeroPhoto
    ? isV2
    : isV2 || isHero || theme.textColor === '#FFFFFF'
  // NUCLEAR: Immediately scrub product data on prop changes to prevent slugs from appearing
  // This ensures that even if parent component passes unsanitized data, we clean it immediately
  // CRITICAL: This runs on every render to catch any data updates that might restore slugs
  // CRITICAL: Only return empty if it's STILL a slug AFTER scrubbing, not before
  const safeProduct = useMemo(() => {
    // Verified catalog SKU titles keep product-type hyphens (e.g. No-Pull).
    const preserveSkuTitle =
      (product as { visualRole?: string }).visualRole === 'sku'
    // Scrub first, then check if result is still a slug
    const scrubbedTitle = product.title
      ? preserveSkuTitle
        ? String(product.title).trim()
        : nuclearScrubSlug(product.title)
      : undefined
    const scrubbedName = product.name
      ? preserveSkuTitle
        ? String(product.name).trim()
        : nuclearScrubSlug(product.name)
      : undefined
    const scrubbedSubcategory = product.subcategory ? nuclearScrubSlug(product.subcategory) : undefined
    const scrubbedCategory = product.category ? nuclearScrubSlug(product.category) : undefined
    
    return {
      ...product,
      // Only set to empty if scrubbed result is STILL a slug (shouldn't happen, but safety check)
      title:
        preserveSkuTitle
          ? scrubbedTitle
          : scrubbedTitle && isSlugPattern(scrubbedTitle)
            ? ''
            : scrubbedTitle,
      name:
        preserveSkuTitle
          ? scrubbedName
          : scrubbedName && isSlugPattern(scrubbedName)
            ? ''
            : scrubbedName,
      subcategory: scrubbedSubcategory && isSlugPattern(scrubbedSubcategory) ? '' : scrubbedSubcategory,
      category: scrubbedCategory && isSlugPattern(scrubbedCategory) ? '' : scrubbedCategory,
    }
  }, [product.id, product.title, product.name, product.subcategory, product.category, product.suitabilityReason, product.matchScore, product.finalUrl, product.url, product.affiliateUrl, product.trackingUrl, product.brand, product.provider, product.merchant, product.reasoning, product.aiReason, product.reason, (product as { categoryImageUrl?: string }).categoryImageUrl, (product as { visualRole?: string }).visualRole, (product as { imageUrl?: string }).imageUrl, (product as { image?: string }).image])
  
  // DEBUG: Log agentic data (dev only to prevent flickering)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && (safeProduct.suitabilityReason || safeProduct.matchScore !== undefined)) {
      console.log('[ProductCardGlass] Agentic Data Received:', {
        productId: safeProduct.id,
        productTitle: safeProduct.title || safeProduct.name,
        hasMatchScore: safeProduct.matchScore !== undefined,
        matchScore: safeProduct.matchScore,
        hasSuitabilityReason: !!safeProduct.suitabilityReason,
        suitabilityReason: safeProduct.suitabilityReason,
        isHero,
      })
    }
  }, [safeProduct.id, safeProduct.matchScore, safeProduct.suitabilityReason, isHero])
  
  // NUCLEAR: Runtime DOM cleanup - remove any elements containing ONLY slug text
  // This is the absolute last line of defense - runs after render to catch any slugs that slip through
  // CRITICAL: Only hide elements that contain ONLY slug text, not valid content
  // CRITICAL: Only run on client side (document is not available during SSR)
  // CRITICAL: Run on EVERY render and data change to catch slugs that reappear
  useEffect(() => {
    // Guard: Only run on client side
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return
    }
    
    const slugPatterns = [
      'Toys-chew-to',
      'Toys-fetch-t',
      'Toys-puzzle-',
      'Toys-rope-',
      'Toys-treat-',
      'Toys-variety',
      'Toys-interac',
      'Toys-all-toy',
      'Toys-tennis-',
      'Walk-essenti',
      'Health-welln',
      'Food-nutriti',
      'Beds-furnitu',
      'toys-chew-to',
      'toys-fetch-t',
      'walk-essenti',
      'health-welln',
      'food-nutriti',
      'beds-furnitu',
    ]
    
    const slugRegex = /^(toys|walk|health|food|beds)-[a-z0-9-]+$/i
    const slugRegexAnywhere = /(toys|walk|health|food|beds)-[a-z0-9-]+/i
    
    const hideSlugElements = () => {
      // Guard: Check document is available
      if (typeof document === 'undefined') return
      
      // CRITICAL: Only target product titles and category badges, NOT subcategory headers
      // Subcategory headers for Toys should show (e.g., "Chew Toys", "Fetch Toys")
      // Only hide exact technical slugs like "Toys-chew-to", "Health Welln", etc.
      
      // Target product titles specifically
      const productTitleElements = document.querySelectorAll('[data-product-title]')
      productTitleElements.forEach((el) => {
        const text = (el.textContent || '').trim()
        const dataTitle = (el as HTMLElement).getAttribute('data-product-title') || ''
        
        // Only hide if it's an exact slug match (not formatted subcategories)
        const isExactSlug = slugPatterns.some(slug => 
          text === slug || 
          dataTitle === slug ||
          text.toLowerCase() === slug.toLowerCase() ||
          dataTitle.toLowerCase() === slug.toLowerCase()
        )
        
        // Check if text matches slug regex patterns (exact slug format)
        const matchesSlugRegex = slugRegex.test(text) || slugRegex.test(dataTitle)
        
        // Only hide exact slugs, not formatted subcategories like "Chew Toys"
        if ((isExactSlug || matchesSlugRegex) && text.length < 25) {
          try {
            ;(el as HTMLElement).style.display = 'none'
            ;(el as HTMLElement).style.visibility = 'hidden'
            ;(el as HTMLElement).style.opacity = '0'
            ;(el as HTMLElement).style.height = '0'
            ;(el as HTMLElement).style.width = '0'
            ;(el as HTMLElement).style.overflow = 'hidden'
            ;(el as HTMLElement).setAttribute('data-slug-hidden', 'true')
          } catch (e) {
            // Silently fail
          }
        }
      })
      
      // Also target category badges that might show slugs like "Health Welln"
      // BUT exclude valid subcategory headers (marked with data-valid-subcategory)
      const categoryBadges = document.querySelectorAll('span[class*="rounded-full"]:not([data-valid-subcategory]), div[class*="rounded-full"]:not([data-valid-subcategory])')
      categoryBadges.forEach((el) => {
        // Skip if it's a valid subcategory header
        if ((el as HTMLElement).getAttribute('data-valid-subcategory') === 'true') {
          return
        }
        
        const text = (el.textContent || '').trim()
        
        // Check for truncated slugs like "Health Welln", "Walk Essenti"
        const isTruncatedSlug = slugPatterns.some(slug => {
          const slugLower = slug.toLowerCase()
          const textLower = text.toLowerCase()
          // Match truncated versions (e.g., "Health Welln" from "Health-welln")
          return textLower === slugLower || 
                 textLower === slugLower.replace(/-/g, ' ') ||
                 (slugLower.includes('welln') && textLower === 'health welln') ||
                 (slugLower.includes('essenti') && textLower === 'walk essenti') ||
                 (slugLower.includes('nutriti') && textLower === 'food nutriti') ||
                 (slugLower.includes('furnitu') && textLower === 'beds furnitu')
        })
        
        // Also check for exact slug regex matches
        const matchesSlugRegex = slugRegex.test(text) || slugRegexAnywhere.test(text)
        
        // Hide if it's a truncated slug or exact slug (short text only)
        // BUT NOT if it's a valid formatted subcategory like "Chew Toys", "Fetch Toys"
        const isFormattedSubcategory = /^(chew|fetch|puzzle|rope|treat|variety|interactive|tennis)\s+toys$/i.test(text)
        
        if ((isTruncatedSlug || matchesSlugRegex) && text.length < 20 && !isFormattedSubcategory) {
          try {
            ;(el as HTMLElement).style.display = 'none'
            ;(el as HTMLElement).style.visibility = 'hidden'
            ;(el as HTMLElement).style.opacity = '0'
            ;(el as HTMLElement).style.height = '0'
            ;(el as HTMLElement).style.width = '0'
            ;(el as HTMLElement).style.overflow = 'hidden'
            ;(el as HTMLElement).setAttribute('data-slug-hidden', 'true')
          } catch (e) {
            // Silently fail
          }
        }
      })
    }
    
    // Run immediately and multiple times to catch all updates
    hideSlugElements()
    const timeouts = [
      setTimeout(hideSlugElements, 50),
      setTimeout(hideSlugElements, 100),
      setTimeout(hideSlugElements, 200),
      setTimeout(hideSlugElements, 500),
      setTimeout(hideSlugElements, 1000),
    ]
    
    // Use MutationObserver to catch dynamically added content
    const observer = new MutationObserver(() => {
      hideSlugElements()
    })
    
    // Observe the entire document for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['data-product-title'],
    })
    
    // Continuous monitoring as safety net - runs every 200ms (dev only to prevent flickering)
    const interval = process.env.NODE_ENV === 'development' 
      ? setInterval(hideSlugElements, 200)
      : null
    
    return () => {
      observer.disconnect()
      if (interval) clearInterval(interval)
      timeouts.forEach(clearTimeout)
    }
  }, [safeProduct.id, safeProduct.title, safeProduct.name, safeProduct.subcategory, safeProduct.category])
  
  // Format subcategory for display (remove underscores, capitalize, handle special cases)
  // MUST be defined before formatProductName uses it
  // CRITICAL: Clean up subcategory names - apply nuclear regex scrubbing first
  // NUCLEAR: Never return slug text - if it's still a slug after scrubbing, return empty
  const formatSubcategory = (subcat: string): string => {
    if (!subcat) return ''
    
    // CRITICAL: Extract meaningful subcategory from slugs like "Toys-chew-to" → "Chew Toys"
    // First, check if it's a Toys slug pattern and extract the subcategory part
    // Handle both "Toys-chew-to" and already-scrubbed "chew-to" patterns
    // Also handle patterns like "Toys-chew-to", "toys-chew-to", "chew-to", etc.
    const toysSlugMatch = subcat.match(/^(?:toys[-_]?)?([a-z0-9-]+)/i)
    if (toysSlugMatch) {
      const subcategoryPart = toysSlugMatch[1]
      
      // Map common subcategory parts to readable names
      const subcategoryMap: Record<string, string> = {
        'chew-to': 'Chew Toys',
        'chew': 'Chew Toys',
        'fetch-t': 'Fetch Toys',
        'fetch': 'Fetch Toys',
        'puzzle-': 'Puzzle Toys',
        'puzzle': 'Puzzle Toys',
        'rope-': 'Rope Toys',
        'rope': 'Rope Toys',
        'rope-&-': 'Rope Toys',
        'treat-': 'Treat Dispensers',
        'treat': 'Treat Dispensers',
        'treat-d': 'Treat Dispensers',
        'treat-dispens': 'Treat Dispensers',
        'variety': 'Variety Packs',
        'interac': 'Interactive Toys',
        'all-toy': 'Toys',
        'tennis-': 'Tennis Balls',
        'tennis': 'Tennis Balls',
        'plush': 'Plush Toys',
        'interactive': 'Interactive Toys',
      }
      
      // Normalize: remove trailing hyphens and convert to lowercase
      const normalized = subcategoryPart.toLowerCase().replace(/-+$/, '').trim()
      
      // Check exact match first
      if (subcategoryMap[normalized]) {
        return subcategoryMap[normalized]
      }
      
      // Check if it starts with any key (handles "chew-to" when map has "chew")
      for (const [key, value] of Object.entries(subcategoryMap)) {
        if (normalized.startsWith(key) || key.startsWith(normalized)) {
          return value
        }
        // Also check if normalized contains the key (handles "treat-dispens" containing "treat-d")
        if (normalized.includes(key) && key.length >= 4) {
          return value
        }
      }
      
      // If not in map, format it: "chew-to" → "Chew Toys"
      const formatted = subcategoryPart
        .replace(/-+$/, '') // Remove trailing hyphens
        .replace(/[-_&]+/g, ' ') // Replace hyphens, underscores, and & with spaces
        .split(' ')
        .filter(word => word.length > 0) // Remove empty strings
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
      
      // Add "Toys" suffix if it doesn't already have it and it's not empty
      if (formatted && !formatted.toLowerCase().includes('toy') && !formatted.toLowerCase().includes('treat') && !formatted.toLowerCase().includes('ball')) {
        return formatted + ' Toys'
      }
      
      // Return formatted if it's valid, otherwise empty
      return formatted || ''
    }
    
    // NUCLEAR REGEX SCRUBBING: Use centralized utility for non-Toys slugs
    // This ensures "Walk-essenti" becomes "Walk" before any formatting
    let cleaned = nuclearScrubSlug(subcat)
    
    // NUCLEAR CHECK: If it's still a slug pattern after scrubbing, return empty
    if (isSlugPattern(cleaned)) {
      return ''
    }
    
      // CRITICAL: Remove common prefixes and suffixes
      // Handle cases like "Dog-collars-", "Dog-toys:fet", "Dog-toys:puz", "Dog-toys:var", "Dog-toys:tre", etc.
      // Remove "Dog-" or "dog-" prefix (case-insensitive, handles "Dog-collars-" -> "collars-")
    cleaned = cleaned.replace(/^dog[-_]/i, '')
      // Remove colon suffixes with 3-4 lowercase letters (e.g., ":fet", ":puz", ":plu", ":tre", ":var")
      // Pattern: colon followed by 3-4 lowercase letters at end of string OR anywhere
      .replace(/:[a-z]{3,4}$/i, '') // At end
      .replace(/:[a-z]{3,4}/gi, '') // Anywhere else
      // Remove trailing dashes, underscores, and colons (e.g., "collars-" -> "collars", "toys:" -> "toys")
      .replace(/[-_:]+$/, '')
      // Remove leading dashes, underscores, and colons
      .replace(/^[-_:]+/, '')
      .trim()
    
    // If after cleaning we still have "Dog-" prefix (edge case), remove it again
    if (cleaned.toLowerCase().startsWith('dog-') || cleaned.toLowerCase().startsWith('dog_')) {
      cleaned = cleaned.replace(/^dog[-_]/i, '').trim()
    }
    
    // Final pass: remove any remaining "Dog-" patterns anywhere in the string (handles edge cases)
    cleaned = cleaned.replace(/dog[-_]/gi, '').trim()
    
    // NUCLEAR CHECK: Final validation - if it's still a slug, return empty
    if (isSlugPattern(cleaned)) {
      return ''
    }
    
    // CRITICAL: Log the transformation for debugging (dev only)
    if (subcat !== cleaned && cleaned && process.env.NODE_ENV === 'development') {
      console.log('[ProductCardGlass] 🏷️ Subcategory cleaning:', {
        original: String(subcat),
        cleaned: String(cleaned),
      })
    }
    
    // NUCLEAR: Never return empty string as fallback - if cleaning removes everything, return empty
    if (!cleaned) return ''
    
    // Handle special cases
    const specialCases: Record<string, string> = {
      'tie-out': 'Tie-Outs',
      'tie_out': 'Tie-Outs',
      'tieouts': 'Tie-Outs',
      'id_tags': 'ID Tags',
      'id-tags': 'ID Tags',
      'idtags': 'ID Tags',
      'collars': 'Collars & Harnesses',
      'harnesses': 'Collars & Harnesses',
      'collars-harnesses': 'Collars & Harnesses',
      'toys': 'Toys',
      'puzzle': 'Puzzle Toys',
      'plush': 'Plush Toys',
      'fetch': 'Fetch Toys',
      'treat': 'Treats',
      'treats': 'Treats',
    }
    
    const normalized = cleaned.toLowerCase().replace(/_/g, '-')
    if (specialCases[normalized]) {
      return specialCases[normalized]
    }
    
    // Format: capitalize first letter of each word
    const formatted = cleaned
      .replace(/_/g, ' ')
      .replace(/-/g, ' ')
      .split(' ')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
    
    // NUCLEAR CHECK: Final validation - if formatted result is still a slug, return empty
    // This prevents technical slugs like "Toys-chew-to" from being displayed
    if (isSlugPattern(formatted)) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[ProductCardGlass] 🚨 PRODUCTION BLOCKER: Formatted subcategory is still a slug:', {
          original: subcat,
          cleaned: cleaned,
          formatted: formatted,
        })
      }
      return ''
    }
    
    // CRITICAL: Log subcategory formatting for debugging (dev only)
    if (subcat !== formatted && formatted && process.env.NODE_ENV === 'development') {
      console.log('[ProductCardGlass] 🏷️ Subcategory formatted:', {
        original: subcat,
        cleaned: cleaned,
        formatted: formatted,
      })
    }
    
    return formatted
  }
  
  // Format product name - use subcategory name formatted nicely
  // NUCLEAR SCRUB: Strip slugs from name parameter before processing
  const formatProductName = (name: string, subcategory: string): string => {
    // NUCLEAR SCRUB: Strip slugs from name first using centralized utility
    // CRITICAL: Apply scrubbing multiple times to ensure all slugs are removed
    let scrubbedName = nuclearScrubSlug(name)
    // Double-check: if still a slug after first scrub, scrub again
    if (isSlugPattern(scrubbedName)) {
      scrubbedName = nuclearScrubSlug(scrubbedName)
      // If still a slug, return empty
      if (isSlugPattern(scrubbedName)) {
        return ''
      }
    }
    let scrubbedSubcategory = nuclearScrubSlug(subcategory)
    // Double-check subcategory too
    if (isSlugPattern(scrubbedSubcategory)) {
      scrubbedSubcategory = nuclearScrubSlug(scrubbedSubcategory)
      if (isSlugPattern(scrubbedSubcategory)) {
        scrubbedSubcategory = ''
      }
    }
    
    // If name is a subcategory like "Tie-Outs" or "Dog Collars & Harnesses", format it properly
    const subcatFormatted = formatSubcategory(scrubbedSubcategory)
    
    // CRITICAL: If name contains raw subcategory patterns (Dog-, :var, :tre, etc.), clean it first
    if (scrubbedName) {
      // Check if name looks like a raw subcategory (contains "Dog-" or colon suffixes)
      const hasRawSubcategoryPattern = /dog[-_]/i.test(scrubbedName) || /:[a-z]{3,4}/i.test(scrubbedName)
      if (hasRawSubcategoryPattern) {
        // Clean the name using the same logic as formatSubcategory
        const cleanedName = formatSubcategory(scrubbedName)
        if (cleanedName && cleanedName !== scrubbedName) {
          return cleanedName
        }
      }
      
      // If name is different from subcategory (after cleaning), use it
      if (scrubbedName.toLowerCase() !== scrubbedSubcategory.toLowerCase()) {
        return scrubbedName
      }
    }
    // Otherwise use formatted subcategory
    return subcatFormatted || 'Product'
  }
  
  // NUCLEAR DATA SCRUB: Strip technical slugs from ALL product fields at component entry point
  // This is the final line of defense - scrub everything before any processing
  // PRODUCTION CRITICAL: This must catch ALL slugs or production is blocked
  // Use centralized nuclear scrubbing utility
  // CRITICAL: safeProduct is already scrubbed, but apply additional scrubbing as safety measure
  const rawSubcategory = safeProduct.subcategory || safeProduct.category || ''
  const rawCategory = safeProduct.category || ''
  const rawTitle = safeProduct.title || ''
  const rawName = safeProduct.name || ''
  
  // NUCLEAR SCRUB: Apply centralized slug stripping to all fields immediately
  // CRITICAL: Double-scrub to ensure no slugs slip through
  let subcategory = nuclearScrubSlug(rawSubcategory)
  if (isSlugPattern(subcategory)) {
    subcategory = nuclearScrubSlug(subcategory) // Scrub again
    if (isSlugPattern(subcategory)) subcategory = '' // If still a slug, empty it
  }
  
  let category = nuclearScrubSlug(rawCategory)
  if (isSlugPattern(category)) {
    category = nuclearScrubSlug(category)
    if (isSlugPattern(category)) category = ''
  }
  
  // CRITICAL: Scrub title and name, then combine - ensure no slugs in final product name
  // NUCLEAR: Double-scrub to catch any slugs that slip through
  // SKU cards preserve catalog product-type wording (Step In / Adjustable / No-Pull).
  const preserveSkuProductName =
    (safeProduct as { visualRole?: string }).visualRole === 'sku'
  let scrubbedTitle = preserveSkuProductName
    ? String(rawTitle || '').trim()
    : nuclearScrubSlug(rawTitle)
  if (!preserveSkuProductName && isSlugPattern(scrubbedTitle)) {
    scrubbedTitle = nuclearScrubSlug(scrubbedTitle) // Scrub again
    if (isSlugPattern(scrubbedTitle)) scrubbedTitle = '' // If still a slug, empty it
  }
  
  let scrubbedName = preserveSkuProductName
    ? String(rawName || '').trim()
    : nuclearScrubSlug(rawName)
  if (!preserveSkuProductName && isSlugPattern(scrubbedName)) {
    scrubbedName = nuclearScrubSlug(scrubbedName) // Scrub again
    if (isSlugPattern(scrubbedName)) scrubbedName = '' // If still a slug, empty it
  }
  
  // Use scrubbed values - if both are slugs, result is empty
  // CRITICAL: This is the source of truth for product name - must be slug-free
  const rawProductName = scrubbedName || scrubbedTitle || ''
  
  // CRITICAL: Remove bundle category names from product cards - they're redundant (bundle section headers already show them)
  // Use centralized BUNDLE_NAMES_TO_FILTER from slugScrubber utility
  
  // CRITICAL: Bundle prefix patterns that catch any string starting with bundle name + hyphen
  // This catches patterns like "Toys-", "Walk-", "Health-", "Food-", "Beds-" followed by anything
  const BUNDLE_PREFIX_PATTERNS = [
    /^toys-/i,
    /^walk-/i,
    /^health-/i,
    /^food-/i,
    /^beds-/i,
  ]
  
  // Also check against the bundleTitle prop (dynamic check)
  // This catches cases where bundleTitle might be passed as product name
  // Use centralized BUNDLE_NAMES_TO_FILTER from slugScrubber utility
  const allBundleNamesToCheck = [...BUNDLE_NAMES_TO_FILTER]
  if (bundleTitle && typeof bundleTitle === 'string') {
    // Add bundleTitle and its variations to the check list
    const bundleTitleLower = bundleTitle.toLowerCase().trim()
    if (!allBundleNamesToCheck.some(name => name.toLowerCase() === bundleTitleLower)) {
      allBundleNamesToCheck.push(bundleTitle)
      // Also add normalized version
      const normalizedBundleTitle = bundleTitle.replace(/[^a-z0-9]/gi, '').toLowerCase()
      if (normalizedBundleTitle) {
        allBundleNamesToCheck.push(normalizedBundleTitle)
      }
    }
  }
  
  // Normalize for comparison (remove all non-alphanumeric, lowercase)
  const normalize = (str: string): string => {
    return str.toLowerCase().replace(/[^a-z0-9]/g, '').trim()
  }
  
  // Check if product name matches any bundle category name (exact or truncated)
  // CRITICAL: Also check for hyphenated subcategory slugs (e.g., "Toys-chew-to", "Toys-fetch-t")
  const isBundleCategoryName = rawProductName && (() => {
    if (!rawProductName) return false
    
    const productLower = rawProductName.toLowerCase().trim()
    
    // CRITICAL: First check for bundle prefix patterns (e.g., "Toys-", "Walk-", "Health-", "Food-", "Beds-")
    // This catches any string that starts with a bundle name followed by a hyphen or truncation
    for (const pattern of BUNDLE_PREFIX_PATTERNS) {
      if (pattern.test(productLower)) {
        return true
      }
    }
    
    // Check against all bundle names
    for (const bundleName of allBundleNamesToCheck) {
      if (!bundleName) continue
      
      const bundleLower = String(bundleName).toLowerCase().trim()
      
      // Exact match (case-insensitive)
      if (productLower === bundleLower) return true
      
      // Normalized match (handles "Health & Wellness" vs "Health Wellness")
      if (normalize(rawProductName) === normalize(String(bundleName))) return true
      
      // Check if product name starts with bundle name prefix (catches truncations)
      // Only match if product is clearly a truncation (shorter than bundle)
      if (bundleLower.length >= 6 && productLower.length >= 4 && productLower.length < bundleLower.length) {
        const bundlePrefix = bundleLower.substring(0, Math.min(10, bundleLower.length))
        const productPrefix = productLower.substring(0, Math.min(productLower.length, bundlePrefix.length))
        if (bundlePrefix.startsWith(productPrefix) || productPrefix === bundlePrefix.substring(0, productPrefix.length)) {
          return true
        }
      }
      
      // Check if product contains full bundle name (e.g., "Walk Essentials" in product name)
      if (productLower.includes(bundleLower) && bundleLower.length >= 6) {
        return true
      }
      
      // Check if product name is a prefix of bundle name (catches partial matches)
      if (bundleLower.startsWith(productLower) && productLower.length >= 4 && bundleLower.length > productLower.length) {
        return true
      }
    }
    
    // CRITICAL: Check for hyphenated subcategory slugs (e.g., "Toys-chew-to", "Toys-fetch-t")
    // These patterns indicate the product name is actually a subcategory slug, not a real product name
    const hyphenatedPatterns = [
      /^toys-[a-z-]+$/i,  // Matches "Toys-chew-to", "Toys-fetch-t", etc.
      /^walk-[a-z-]+$/i,  // Matches "Walk-essenti", etc.
      /^health-[a-z-]+$/i, // Matches "Health-welln", etc.
      /^food-[a-z-]+$/i,  // Matches "Food-nutriti", etc.
      /^beds-[a-z-]+$/i,  // Matches "Beds-furnitu", etc.
    ]
    
    for (const pattern of hyphenatedPatterns) {
      if (pattern.test(productLower)) {
        return true
      }
    }
    
    return false
  })()
  
  // REMOVED: Bundle category title from all cards to prevent duplication
  let productTitle: string
  // CRITICAL: rawProductName is already defined above and double-scrubbed
  // NEVER show bundle category names - if product name matches, use formatted subcategory instead
  // CRITICAL: Also check if product name contains any bundle name patterns (even as substring)
  // CRITICAL: Check the scrubbed version, not the original
  const containsBundlePattern = rawProductName && (
    isBundleCategoryName ||
    // Check if product name contains any bundle name as a substring
    allBundleNamesToCheck.some(bundleName => {
      if (!bundleName) return false
      const bundleLower = String(bundleName).toLowerCase().trim()
      const productLower = rawProductName.toLowerCase().trim()
      // Check if product name contains bundle name (handles cases like "Toys-chew-to" containing "Toys")
      return bundleLower.length >= 3 && productLower.includes(bundleLower)
    }) ||
    // Check for hyphenated patterns anywhere in the string (catches "Toys-chew-to", "Walk-essenti", etc.)
    /(toys|walk|health|food|beds)-[a-z-]+/i.test(rawProductName)
  )
  
  // NUCLEAR SCRUB: Apply final slug stripping to rawProductName before any logic
  // PRODUCTION CRITICAL: This must catch ALL slugs or production is blocked
  // Use centralized utility - this ensures even if data wasn't scrubbed upstream, we catch it here
  // SKU study cards keep catalog product-type hyphens (e.g. No-Pull) — names are verified items, not slugs.
  const isSkuProductTitle =
    (safeProduct as { visualRole?: string }).visualRole === 'sku'
  let scrubbedProductName = isSkuProductTitle
    ? String(rawProductName || '').trim()
    : nuclearScrubSlug(rawProductName)
  
  // NUCLEAR: If it's still a slug pattern after scrubbing, empty it completely
  if (
    !isSkuProductTitle &&
    (isSlugPattern(scrubbedProductName) || isBundleName(scrubbedProductName))
  ) {
      scrubbedProductName = ''
  }
  
  // NUCLEAR: Additional check for hyphenated patterns - only if it's an exact slug match
  if (
    !isSkuProductTitle &&
    scrubbedProductName &&
    /^(toys|walk|health|food|beds)-[a-z0-9-]+$/i.test(scrubbedProductName)
  ) {
        scrubbedProductName = ''
  }
  
  // NUCLEAR CLEANUP: Hide technical tags - if scrubbedProductName is a bundle name or subcategory slug, set productTitle to empty
  // CRITICAL: Only hide if it's actually a slug, not valid content
  // Check if scrubbedProductName exists and is valid before checking for slugs
  if (!scrubbedProductName || !scrubbedProductName.trim()) {
    // If no product name, set to empty (this is fine - some products might not have names)
    productTitle = ''
  } else if (isSkuProductTitle) {
    // Verified catalog SKU title — preserve product-type wording as supplied.
    productTitle = scrubbedProductName
  } else {
    // Only hide if it's actually a slug or bundle name
    const isSlugOrBundle = containsBundlePattern || 
                           isSlugPattern(scrubbedProductName) || 
                           isBundleName(scrubbedProductName) ||
                           /^(toys|walk|health|food|beds)-[a-z0-9-]+$/i.test(scrubbedProductName)
    
    if (isSlugOrBundle) {
      // NUCLEAR: Only set to empty if it's actually a slug/bundle name
      productTitle = ''
    
    // DEBUG: Log when filtering out bundle names (dev only)
    if (process.env.NODE_ENV === 'development' && rawProductName) {
        console.log('[ProductCardGlass] 🚫 NUCLEAR: Filtered out slug/bundle name:', {
          productId: safeProduct.id,
        rawProductName,
        scrubbedProductName,
        isBundleCategoryName,
        containsBundlePattern,
          isSlug: isSlugPattern(scrubbedProductName),
          isBundle: isBundleName(scrubbedProductName),
        productTitle: '', // Always empty for slugs
        bundleId,
        bundleTitle,
      })
    }
  } else {
    // Show the actual product name (e.g., "Collars", "Harnesses", "Supplements", "Skin & Coat")
    // Use scrubbed version to ensure no slugs slip through
    productTitle = scrubbedProductName
    }
  }
  
  // CRITICAL: For Toys bundle, use formatted subcategory as the title
  // This ensures Toys cards show subcategory as bold header (matching other bundles' visual approach)
  // SKU carousel cards keep the catalog product name; subcategory is shown via subcategoryLabel.
  const bundleTitleLower = bundleTitle?.toLowerCase() || ''
  const bundleIdLower = bundleId?.toLowerCase() || ''
  // More aggressive detection - check for "toys" anywhere in bundle identifiers
  const isToysBundle = bundleTitleLower === 'toys' || 
                       bundleIdLower === 'toys' ||
                       bundleTitleLower.includes('toy') ||
                       bundleIdLower.includes('toy') ||
                       bundleIdLower === 'toys' ||
                       bundleIdLower.includes('toys') ||
                       (bundleId && String(bundleId).toLowerCase().includes('toy'))
  const isSkuVisualRole =
    (safeProduct as { visualRole?: string }).visualRole === 'sku'
  
  if (isToysBundle && !isSkuVisualRole) {
    // Use originalSubcategory from product (preserved in PicksTab), or fallback to other fields
    // CRITICAL: Check multiple sources to ensure we get the subcategory data
    // Also try to extract from product ID if it contains subcategory info (e.g., "rec-toys-chew-to-0")
    let originalSubcategory = (product as any).originalSubcategory || 
                               product.subcategory || 
                               product.category || 
                               safeProduct.subcategory || 
                               safeProduct.category || 
                               product.title ||
                               product.name ||
                               rawProductName ||
                               ''
    
    // FALLBACK 1: Try to extract from product ID (e.g., "rec-toys-chew-to-0", "toys-chew-to-0")
    if (!originalSubcategory || originalSubcategory.trim() === '') {
      if (product.id) {
        // Try multiple ID patterns
        const idPatterns = [
          /toys[-_]([a-z0-9-]+)/i,  // "toys-chew-to" or "toys_chew_to"
          /rec[-_]toys[-_]([a-z0-9-]+)/i,  // "rec-toys-chew-to"
          /([a-z]+)[-_]toys?/i,  // "chew-toys" or "fetch-toys"
        ]
        
        for (const pattern of idPatterns) {
          const idMatch = product.id.match(pattern)
          if (idMatch) {
            originalSubcategory = idMatch[1] || `toys-${idMatch[0]}`
            break
          }
        }
      }
    }
    
    // FALLBACK 2: Try to extract from URL if ID didn't work
    if (!originalSubcategory || originalSubcategory.trim() === '') {
      const url = product.finalUrl || product.affiliateUrl || product.url || ''
      if (url) {
        // Look for subcategory patterns in URL
        const urlMatch = url.match(/(?:toys?|toy)[-_/]([a-z0-9-]+)/i)
        if (urlMatch) {
          originalSubcategory = urlMatch[1]
        }
      }
    }
    
    // FALLBACK 3: Try to infer from product name/title if it contains toy-related keywords
    if (!originalSubcategory || originalSubcategory.trim() === '') {
      const nameOrTitle = (product.name || product.title || rawProductName || '').toLowerCase()
      const toyKeywords: Record<string, string> = {
        'chew': 'chew-to',
        'fetch': 'fetch-t',
        'puzzle': 'puzzle-',
        'rope': 'rope-',
        'treat': 'treat-',
        'tennis': 'tennis-',
        'plush': 'plush',
        'interactive': 'interactive',
        'variety': 'variety',
      }
      
      for (const [keyword, subcat] of Object.entries(toyKeywords)) {
        if (nameOrTitle.includes(keyword)) {
          originalSubcategory = subcat
          break
        }
      }
    }
    
    const formattedSubcategory = formatSubcategory(originalSubcategory)
    
    // DEBUG: Log all Toys cards to see what data we have (dev only)
    if (process.env.NODE_ENV === 'development') {
      console.log('[ProductCardGlass] 🧸 Toys card data check:', {
      productId: product.id,
      originalSubcategory,
      formattedSubcategory,
      productTitle,
      productSubcategory: product.subcategory,
      productCategory: product.category,
      productTitleField: product.title,
      productNameField: product.name,
      rawProductName,
      isSlug: isSlugPattern(productTitle),
      isBundle: isBundleName(productTitle),
      bundleTitle,
      bundleId,
      isToysBundle,
      hasOriginalSubcategory: !!(product as any).originalSubcategory,
      })
    }
    
    // CRITICAL: For Toys bundle, ALWAYS prefer formatted subcategory as title when available
    // This ensures consistent visual approach across all Toys cards
    // Use formatted subcategory if:
    // 1. productTitle is empty/missing, OR
    // 2. productTitle is a slug/bundle name, OR
    // 3. formattedSubcategory is valid (regardless of productTitle)
    const shouldUseSubcategory = (!productTitle || productTitle.trim() === '') ||
                                 isSlugPattern(productTitle) ||
                                 isBundleName(productTitle) ||
                                 (formattedSubcategory && 
                                  formattedSubcategory.trim() !== '' &&
                                  formattedSubcategory.toLowerCase() !== 'toys' &&
                                  formattedSubcategory.toLowerCase() !== 'toy' &&
                                  !isSlugPattern(formattedSubcategory))
    
    // CRITICAL: Always use formatted subcategory for Toys if it's valid, even if productTitle exists
    // This ensures all Toys cards show subcategory headers consistently
    // FIX: More aggressive fallback - if formattedSubcategory is empty but we have originalSubcategory,
    // try to format it again with a more lenient approach
    let finalFormattedSubcategory = formattedSubcategory
    
    // FALLBACK: If formattedSubcategory is empty but we have originalSubcategory, try harder
    if ((!finalFormattedSubcategory || finalFormattedSubcategory.trim() === '') && originalSubcategory && originalSubcategory.trim() !== '') {
      // Try direct formatting without strict validation
      const directFormat = originalSubcategory
        .replace(/^(?:toys?[-_])?/i, '') // Remove "toys-" or "toy-" prefix
        .replace(/[-_&]+/g, ' ') // Replace separators with spaces
        .split(' ')
        .filter((word: string) => word.length > 0)
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
      
      // Add "Toys" suffix if it doesn't already have it
      if (directFormat && !directFormat.toLowerCase().includes('toy') && !directFormat.toLowerCase().includes('treat') && !directFormat.toLowerCase().includes('ball')) {
        finalFormattedSubcategory = directFormat + ' Toys'
      } else if (directFormat) {
        finalFormattedSubcategory = directFormat
      }
    }
    
    // Use formatted subcategory if it's valid
    // CRITICAL: For Toys bundle, ALWAYS try to use a subcategory header, even if we have to use a generic one
    if (finalFormattedSubcategory && 
        finalFormattedSubcategory.trim() !== '' &&
        finalFormattedSubcategory.toLowerCase() !== 'toys' &&
        finalFormattedSubcategory.toLowerCase() !== 'toy' &&
        !isSlugPattern(finalFormattedSubcategory)) {
      productTitle = finalFormattedSubcategory
      
      // DEBUG: Log when we're using formatted subcategory as title (dev only)
      if (process.env.NODE_ENV === 'development') {
        console.log('[ProductCardGlass] ✅ Using formatted subcategory as title:', {
        productId: product.id,
        originalSubcategory,
        formattedSubcategory,
        finalFormattedSubcategory,
        previousProductTitle: productTitle,
        })
      }
    } else {
      // FALLBACK: If we still don't have a subcategory, use a generic "Toys" header for Toys bundle
      // This ensures ALL Toys cards have SOME header, even if we can't determine the specific subcategory
      if (isToysBundle && (!productTitle || productTitle.trim() === '' || isSlugPattern(productTitle) || isBundleName(productTitle))) {
        // Use generic "Toys" as last resort - better than nothing
        productTitle = 'Toys'
        if (process.env.NODE_ENV === 'development') {
          console.log('[ProductCardGlass] ⚠️ Using generic "Toys" header as fallback:', {
          productId: product.id,
          originalSubcategory,
          formattedSubcategory,
          finalFormattedSubcategory,
          })
        }
      }
      
      // DEBUG: Log when we're NOT using formatted subcategory (dev only)
      if (process.env.NODE_ENV === 'development') {
        console.log('[ProductCardGlass] ❌ NOT using formatted subcategory:', {
        productId: product.id,
        originalSubcategory,
        formattedSubcategory,
        finalFormattedSubcategory,
        productTitle,
        shouldUseSubcategory,
        isToysBundle,
        reasons: {
          isEmpty: !productTitle || productTitle.trim() === '',
          isSlug: isSlugPattern(productTitle),
          isBundle: isBundleName(productTitle),
          hasFormattedSubcategory: !!finalFormattedSubcategory && finalFormattedSubcategory.trim() !== '',
          isNotToys: finalFormattedSubcategory && finalFormattedSubcategory.toLowerCase() !== 'toys' && finalFormattedSubcategory.toLowerCase() !== 'toy',
          isNotSlug: finalFormattedSubcategory && !isSlugPattern(finalFormattedSubcategory),
        },
      })
      }
    }
  }
  
  // DEBUG: Always log when bundle name might be in product data
  useEffect(() => {
    if (rawProductName && process.env.NODE_ENV === 'development') {
      const mightBeBundle = BUNDLE_NAMES_TO_FILTER.some((bn: string) => 
        rawProductName.toLowerCase().includes(bn.toLowerCase()) || 
        bn.toLowerCase().includes(rawProductName.toLowerCase())
      )
      if (mightBeBundle) {
        console.log('[ProductCardGlass] ⚠️ Product name might be bundle name:', {
          productId: product.id,
          rawProductName,
          isBundleCategoryName,
          productTitleResult: productTitle,
          bundleId,
          bundleTitle,
        })
      }
    }
  }, [product.id, rawProductName, isBundleCategoryName, productTitle, bundleId, bundleTitle])
  // CRITICAL: Split linking logic
  // Hero Cards: production category-level page via getBundleCategoryUrl when a
  // separate subcategoryBrowseUrl is present (SKU-cache preview).
  // Legacy hybrid study (no subcategoryBrowseUrl) keeps category_representative
  // subcategory destination on the hero CTA itself.
  // Carousel / SKU cards: product-specific URL.
  // getBundleCategoryUrl: static import
  const productVisualRole = (safeProduct as { visualRole?: string }).visualRole
  const productDestinationUrl =
    safeProduct.finalUrl ||
    safeProduct.trackingUrl ||
    safeProduct.affiliateUrl ||
    safeProduct.url ||
    '#'
  const separateSubcategoryBrowse = String(
    (safeProduct as { subcategoryBrowseUrl?: string }).subcategoryBrowseUrl ||
      ''
  ).trim()
  
  let affiliateUrl: string
  if (isHero && bundleId) {
    const categoryUrl = getBundleCategoryUrl(bundleId)
    if (separateSubcategoryBrowse.startsWith('http') && categoryUrl) {
      // Preview path: hero = production bundle category; Explore harnesses is separate.
      affiliateUrl = categoryUrl
    } else if (
      productVisualRole === 'category_representative' &&
      productDestinationUrl.startsWith('http')
    ) {
      affiliateUrl = productDestinationUrl
    } else {
      affiliateUrl = categoryUrl || productDestinationUrl
    }
    
    if (process.env.NODE_ENV === 'development' && categoryUrl) {
      console.log('[ProductCardGlass] 🎯 Hero card destination:', {
        bundleId,
        affiliateUrl,
        categoryUrl,
        separateSubcategoryBrowse: separateSubcategoryBrowse || null,
        productTitle: product.title || product.name,
      })
    }
  } else {
    affiliateUrl = productDestinationUrl
  }
  
  // Validate URL - ensure it's a real link, not a placeholder
  // Accept Chewy, merchantreviews.net, or Google Shopping URLs
  const isValidUrl = affiliateUrl && 
                     affiliateUrl !== '#' && 
                     affiliateUrl.startsWith('http') &&
                     (affiliateUrl.includes('chewy.com') ||
                      affiliateUrl.includes('sjv.io') ||
                      affiliateUrl.includes('merchantreviews.net') ||
                      affiliateUrl.includes('google.com/search?tbm=shop'))
  
  if (!isValidUrl && process.env.NODE_ENV === 'development') {
    console.warn('[ProductCardGlass] ⚠️ Invalid or missing product URL:', {
      productId: product.id,
      productTitle: product.title || product.name,
      isHero,
      bundleId,
      affiliateUrl,
      finalUrl: product.finalUrl,
      trackingUrl: product.trackingUrl,
      affiliateUrlProp: product.affiliateUrl,
      url: product.url,
    })
  }
  
  // Determine affiliate partner name from URL or product data
  // CRITICAL: Use partner name instead of category/subcategory to avoid showing "Beds Furnitu", "Food Nutriti", etc.
  const getAffiliatePartnerName = (): string => {
    // Priority 1: Check URL for known retailers
    if (affiliateUrl && affiliateUrl.includes('google.com/search?tbm=shop')) {
      return 'Google Shopping'
    }
    if (
      affiliateUrl &&
      (affiliateUrl.includes('chewy.com') || affiliateUrl.includes('sjv.io'))
    ) {
      return 'Chewy'
    }
    if (affiliateUrl && affiliateUrl.includes('amazon.com')) {
      return 'Amazon'
    }
    if (affiliateUrl && (affiliateUrl.includes('petcaresupplies.com') || affiliateUrl.includes('petcare-supplies.com'))) {
      return 'Petcare Supplies'
    }
    
    // Priority 2: Check safeProduct.provider field
    if (safeProduct.provider) {
      const provider = String(safeProduct.provider).toLowerCase()
      if (provider === 'google_shopping' || provider === 'google-shopping') return 'Google Shopping'
      if (provider === 'chewy') return 'Chewy'
      if (provider === 'amazon') return 'Amazon'
      if (provider === 'petcare-supplies' || provider === 'petcare_suppliers') return 'Petcare Supplies'
      // Capitalize first letter if unknown provider
      return provider.charAt(0).toUpperCase() + provider.slice(1)
    }
    
    // Priority 3: Check safeProduct.merchant field
    if (safeProduct.merchant) {
      const merchant = String(safeProduct.merchant).toLowerCase()
      if (merchant === 'google_shopping' || merchant === 'google-shopping') return 'Google Shopping'
      if (merchant === 'chewy' || merchant === 'chewy.com') return 'Chewy'
      if (merchant === 'amazon' || merchant === 'amazon.com') return 'Amazon'
      if (merchant === 'petcare-supplies' || merchant === 'petcare_suppliers') return 'Petcare Supplies'
      // Capitalize first letter if unknown merchant
      return merchant.charAt(0).toUpperCase() + merchant.slice(1)
    }
    
    // Fallback: Default to "Chewy" since most products are from Chewy
    return 'Chewy'
  }
  
  const affiliatePartnerName = getAffiliatePartnerName()
  
  // Brand name - removed Chewy branding for cleaner Wagamour brand experience
  const brandName = safeProduct.brand || ''
  
  // Get emoji for mini cards (non-hero cards only)
  // getEmojiForProduct: static import
  // NUCLEAR SCRUB: Use scrubbed category/subcategory for emoji lookup
  const productEmoji = getEmojiForProduct(category, subcategory)
  const visualRole = (safeProduct as { visualRole?: string }).visualRole
  const isSkuCard = visualRole === 'sku'
  const isSkuEnriched =
    (safeProduct as { skuEnriched?: boolean }).skuEnriched === true
  // SKU carousel titles may wrap to 2 lines; reserve that height so CTAs stay aligned.
  const titleLineClamp = isHero || isSkuCard ? 2 : 1
  const titleClampClass = titleLineClamp === 2 ? 'line-clamp-2' : 'line-clamp-1'
  const skuTitleBlockMinHeight = isSkuCard ? '2.5rem' : undefined
  const categoryRepUrl =
    (safeProduct as { categoryImageUrl?: string }).categoryImageUrl ||
    (visualRole === 'category_representative' || isSkuCard
      ? (safeProduct as { imageUrl?: string; image?: string }).imageUrl ||
        (safeProduct as { image?: string }).image
      : undefined)
  const showCategoryImageryWell = !isHero || !!categoryRepUrl
  const heroTitleAboveImage = Boolean(isHero && categoryRepUrl && !isSkuCard)

  const formatVerifiedPrice = (): string | null => {
    const rawPrice = (safeProduct as { price?: number | string }).price
    const currency = String(
      (safeProduct as { currency?: string }).currency || ''
    ).trim()
    if (rawPrice === undefined || rawPrice === null || rawPrice === '') return null
    const numeric =
      typeof rawPrice === 'number' ? rawPrice : Number.parseFloat(String(rawPrice))
    if (!Number.isFinite(numeric)) return null
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numeric)
    } catch {
      return currency ? `${numeric.toFixed(2)} ${currency}` : numeric.toFixed(2)
    }
  }
  const verifiedPriceLabel =
    isSkuCard || isSkuEnriched ? formatVerifiedPrice() : null
  const verifiedCurrency =
    isSkuCard || isSkuEnriched
      ? String((safeProduct as { currency?: string }).currency || '').trim()
      : ''
  const representativeCatalogName = String(
    (safeProduct as { representativeCatalogName?: string })
      .representativeCatalogName || ''
  ).trim()
  const viewProductUrl = String(
    (safeProduct as { viewProductUrl?: string }).viewProductUrl || ''
  ).trim()
  const viewProductCta = String(
    (safeProduct as { viewProductCta?: string }).viewProductCta || 'View product'
  ).trim()
  // Hero keeps Shop-on-category as primary; View product is a separate link.
  // Non-hero enriched cards use View product as the primary CTA instead.
  const hasViewProduct =
    isSkuEnriched &&
    !!isHero &&
    !!viewProductUrl &&
    viewProductUrl.startsWith('http') &&
    !!viewProductCta
  const ctaLabelOverride = String(
    (safeProduct as { ctaLabel?: string }).ctaLabel || ''
  ).trim()
  const subcategoryLabel = String(
    (safeProduct as { subcategoryLabel?: string }).subcategoryLabel ||
      (isSkuCard || isSkuEnriched ? safeProduct.subcategory || '' : '') ||
      ''
  ).trim()
  const subcategoryBrowseUrl = String(
    (safeProduct as { subcategoryBrowseUrl?: string }).subcategoryBrowseUrl ||
      ''
  ).trim()
  const subcategoryBrowseCta = String(
    (safeProduct as { subcategoryBrowseCta?: string }).subcategoryBrowseCta ||
      ''
  ).trim()
  const hasSubcategoryBrowse =
    (isHero || isSkuEnriched) &&
    !!subcategoryBrowseUrl &&
    subcategoryBrowseUrl.startsWith('http') &&
    !!subcategoryBrowseCta
  
  // AGENTIC QUEUE: De-duplicate reasoning sources
  // CRITICAL: suitabilityReason is the most agentic (from Agent's scoring API) and is shown in "Wagamour Pick" badge
  // For "Why this product?" section, only use OTHER reasoning sources to avoid duplication
  // Priority: reasoning (agentic, different from suitabilityReason) > aiReason (legacy) > reason (legacy)
  // EXCLUDE suitabilityReason from this chain since it's already shown in the badge
  let reasoningText = ''
  if (safeProduct.reasoning && safeProduct.reasoning !== safeProduct.suitabilityReason) {
    // Use reasoning if it's different from suitabilityReason
    reasoningText = safeProduct.reasoning
  } else if (safeProduct.aiReason && safeProduct.aiReason !== safeProduct.suitabilityReason) {
    // Use aiReason if it's different from suitabilityReason
    reasoningText = safeProduct.aiReason
  } else if (safeProduct.reason && safeProduct.reason !== safeProduct.suitabilityReason) {
    // Use reason if it's different from suitabilityReason
    reasoningText = safeProduct.reason
  }
  
  // OPTIMIZED: Preserve rich AI reasoning, only filter out truly generic templates
  const personalizeReasoning = (baseReasoning: string): string => {
    if (!baseReasoning || baseReasoning.trim() === '') {
      // If no reasoning exists, return empty
      return ''
    }
    
    const lowerReasoning = baseReasoning.toLowerCase()
    
    // Check if reasoning is generic/evergreen template (the specific pattern we want to avoid)
    const isGenericTemplate = 
      lowerReasoning.includes('addresses her specific needs') ||
      lowerReasoning.includes('addresses his specific needs') ||
      lowerReasoning.includes('addresses their specific needs') ||
      lowerReasoning.includes('requires this product for optimal comfort and safety') ||
      (lowerReasoning.includes('recommended for') && 
       lowerReasoning.includes('at') && 
       lowerReasoning.includes('years old') && 
       (lowerReasoning.includes('requires this product') || lowerReasoning.includes('addresses')) &&
       baseReasoning.trim().length < 100) // Flag generic patterns
    
    // If it's a generic template, return empty (don't show it)
    if (isGenericTemplate) {
      return ''
    }
    
    // Fix obvious issues like "given for" duplication
    if (lowerReasoning.includes('given for')) {
      return baseReasoning.replace(/,\s*given\s+for\s+[^,]+/i, '').trim()
    }
    
    // REASONING CHECK: Ensure temperature/humidity is mentioned for weather-relevant products
    // For 50°F weather, the reasoning should specifically mention temperature/humidity to justify the recommendation
    if (weather?.temperature !== undefined) {
      const temp = weather.temperature
      const humidity = weather.humidity
      const lowerReasoning = baseReasoning.toLowerCase()
      
      // Check if reasoning mentions temperature or weather context
      const mentionsTemperature = lowerReasoning.includes('°f') || 
                                  lowerReasoning.includes('° f') ||
                                  lowerReasoning.includes('temperature') ||
                                  lowerReasoning.includes('temp') ||
                                  lowerReasoning.includes('cold') ||
                                  lowerReasoning.includes('warm') ||
                                  lowerReasoning.includes('hot') ||
                                  lowerReasoning.includes('cool') ||
                                  lowerReasoning.includes('weather')
      
      const mentionsHumidity = humidity !== undefined && (
        lowerReasoning.includes('humidity') ||
        lowerReasoning.includes('humid') ||
        lowerReasoning.includes('moisture')
      )
      
      // For temperatures around 50°F (40-60°F range), ensure temperature is mentioned
      if (temp >= 40 && temp <= 60 && !mentionsTemperature) {
        // Enhance reasoning to include temperature context
        const tempContext = temp < 50 
          ? `With temperatures around ${temp}°F, ` 
          : `At ${temp}°F, `
        return `${tempContext}${baseReasoning}`
      }
      
      // For any temperature, if humidity is provided and not mentioned, add it
      if (humidity !== undefined && !mentionsHumidity && temp > 70) {
        // High humidity is more relevant for hot weather
        return `${baseReasoning} (${humidity}% humidity)`
      }
    }
    
    // Otherwise, preserve the reasoning (even if it doesn't have "because" or is shorter)
    // This ensures we show AI-generated reasoning unless it's clearly a generic template
    return baseReasoning
  }
  
  // Apply personalization (but preserve good AI reasoning)
  // If reasoning is generic/empty, return empty - don't use generic fallback
  reasoningText = personalizeReasoning(reasoningText)

  // CRITICAL: If suitabilityReason exists (shown in "Wagamour Pick" badge), don't show "Why this product?" 
  // unless there's a DIFFERENT reasoning source to avoid duplication
  // This ensures we only show quality AI-generated reasoning, not duplicates
  // NUCLEAR DE-DUPLICATION: If reasoningText matches suitabilityReason (even after personalization), clear it to prevent duplication
  if (safeProduct.suitabilityReason && reasoningText && reasoningText.trim().toLowerCase() === safeProduct.suitabilityReason.trim().toLowerCase()) {
    reasoningText = ''
  }
  
  // REMOVED: displaySubcategory is no longer used - subcategory/bundle_title/slug should NOT be rendered in UI
  // They are redundant because bundle titles are already visible in section headers
  // const displaySubcategory = formatSubcategory(subcategory)

  // AGENTIC REASONING: Generate reasoning badge for high-match products
  const petTraits: PetTraits | null = useMemo(() => {
    if (!dogProfile) return null
    return {
      name: dogProfile.name,
      breed: dogProfile.breed,
      ageYears: dogProfile.ageYears,
      ageBracket: dogProfile.ageBracket,
      size: dogProfile.size,
      hasJointIssues: false, // TODO: Get from dog profile if available
      hasBreathingIssues: false, // TODO: Get from dog profile if available
      hasAllergies: false, // TODO: Get from dog profile if available
      energyLevel: undefined, // TODO: Get from dog profile if available
      weatherTolerance: [],
    }
  }, [dogProfile])

  const weatherContext: WeatherContext | null = useMemo(() => {
    if (!weather) return null
    return {
      temperature: weather.temperature,
      condition: weather.condition,
      humidity: undefined,
    }
  }, [weather])

  const mobilityScore = useMemo(() => 
    calculateMobilityScore(petTraits, weatherContext),
    [petTraits, weatherContext]
  )

  // Safety check: Never recommend high-activity toys for low mobility dogs
  const isSafe = useMemo(() => 
    isProductSafeForMobility(product, mobilityScore),
    [product, mobilityScore]
  )

  // Generate badge (only if product is safe and pet profile exists)
  const reasoningBadge = useMemo(() => {
    if (!isSafe || !petTraits) return null
    return generateReasoningBadge(product, petTraits, weatherContext, mobilityScore)
  }, [product, petTraits, weatherContext, mobilityScore, isSafe])
  
  // Get background image style for hero cards (legacy category photography — skippable via experiment flag)
  const heroBackgroundStyle = showCategoryHeroPhoto
    ? getHeroCardBackgroundStyle(bundleTitle || 'default')
    : {}
  const backgroundImageUrl =
    heroBackgroundStyle.backgroundImage?.replace(/url\(["']?|["']?\)/g, '') || ''
  const hasBackgroundImage =
    showCategoryHeroPhoto &&
    backgroundImageUrl &&
    backgroundImageUrl.trim() !== '' &&
    backgroundImageUrl !== 'none' &&
    backgroundImageUrl.startsWith('http')
  
  if (showCategoryHeroPhoto) {
    const willRender = isHero && !!heroBackgroundStyle.backgroundImage
    console.log('[ProductCardGlass] 🖼️ Hero card background:', {
      bundleTitle: String(bundleTitle || 'undefined'),
      bundleId: String(bundleId || 'undefined'),
      productId: String(safeProduct.id),
      productTitle: String(safeProduct.title || safeProduct.name || 'none'),
      subcategory: String(safeProduct.subcategory || safeProduct.category || 'none'),
      backgroundImage: String(backgroundImageUrl || 'none'),
      hasBackgroundImage: hasBackgroundImage,
      heroBackgroundStyleKeys: Object.keys(heroBackgroundStyle),
      backgroundImageStyle: heroBackgroundStyle.backgroundImage,
      fullHeroBackgroundStyle: heroBackgroundStyle, // Log full style object
      willRenderBackgroundDiv: willRender,
      // CRITICAL: For Toys, log extra details
      ...(bundleTitle === 'toys' ? {
        toysDebug: {
          isHero,
          bundleTitle,
          backgroundImageExists: !!heroBackgroundStyle.backgroundImage,
          backgroundImageValue: heroBackgroundStyle.backgroundImage,
          backgroundImageUrl,
          willRender,
        }
      } : {}),
    })
  }
  
  // CRITICAL: Always render background for legacy photo hero cards
  if (showCategoryHeroPhoto && !hasBackgroundImage) {
    console.warn('[ProductCardGlass] ⚠️ Hero card missing background image:', {
      bundleTitle: String(bundleTitle || 'undefined'),
      bundleId: String(bundleId || 'undefined'),
      heroBackgroundStyle: heroBackgroundStyle,
      backgroundImageUrl: String(backgroundImageUrl || 'none'),
      backgroundImageStyle: heroBackgroundStyle.backgroundImage,
    })
  }
  
  return (
    <>
      <div
        className={`rounded-2xl flex flex-col relative overflow-hidden p-5${!isHero ? ' h-full min-h-full w-full flex-1 self-stretch' : ''}`}
        style={{
          background: !isHero
            ? isV2
              ? picksV2ProductCardStyle.background
              : theme.cardBackground
            : hideCategoryHeroPhoto && isV2
              ? picksV2HeroRecommendationCardStyle.background
              : undefined,
          border:
            isHero && hideCategoryHeroPhoto && isV2
              ? `1px solid ${homeV2Glass.hero.border}`
              : isHero && isV2
                ? picksV2HeroFrameStyle.border
                : `1px solid ${isV2 ? homeV2Glass.nested.border : theme.cardBorder}`,
          boxShadow:
            isHero && hideCategoryHeroPhoto && isV2
              ? picksV2HeroRecommendationCardStyle.boxShadow
              : isHero && isV2
                ? picksV2HeroFrameStyle.boxShadow
                : isV2
                  ? picksV2ProductCardStyle.boxShadow
                  : undefined,
          backdropFilter: isV2 ? `blur(${picksV2GlassBlur})` : 'blur(40px)',
          WebkitBackdropFilter: isV2 ? `blur(${picksV2GlassBlur})` : undefined,
          minHeight: showCategoryHeroPhoto && isHero ? '400px' : undefined,
          position: 'relative',
        }}
      >
        {/* PREMIUM BADGE LAYOUT SYSTEM - Prevents overlaps with proper spacing */}
        {/* Hero Cards: Stack badges vertically in top-right corner */}
        {/* Mini Cards: Contextual badge (top-left), Ad badge (top-right), reasoning badge inline in content */}
        {isHero ? (
          // HERO CARDS: Vertical badge stack in top-right
          <div className="absolute top-2 right-2 z-30 flex flex-col items-end gap-2">
            {/* FTC Compliance: Affiliate Disclosure Badge */}
            {isValidUrl && (() => {
              const isDarkBackground = usesLightHeroText
              const badgeBackground = isDarkBackground
                ? 'rgba(255, 255, 255, 0.25)'
                : 'rgba(0, 0, 0, 0.1)'
              const badgeTextColor = isDarkBackground ? '#FFFFFF' : '#1A1A1A'
              
              return (
                <div 
                  className="px-1.5 py-0.5 rounded text-xs font-medium"
                  style={{
                    background: badgeBackground,
                    color: badgeTextColor,
                    fontSize: '10px',
                    lineHeight: '1.2',
                    fontWeight: 600,
                  }}
                >
                  Ad
                </div>
              )
            })()}
            
            {/* Reasoning Badge (Pack Leader only) - Stacked below Ad badge */}
            {isPackLeader && reasoningBadge && (
              <div
                className="px-2 py-1 rounded-full text-[10px] font-medium backdrop-blur-sm border flex items-center gap-1 max-w-[140px]"
                style={{
                  backgroundColor: `${theme.textColor}15`,
                  borderColor: `${theme.textColor}30`,
                  color: theme.textColor,
                }}
              >
                <AIBadge size="sm" className="flex-shrink-0" />
                <span className="truncate">{reasoningBadge}</span>
              </div>
            )}
          </div>
        ) : (
          // MINI CARDS: Contextual badge handled by BundleSection (top-left), Ad badge (top-right)
          // Reasoning badge removed from absolute positioning to avoid overlap with contextual badge
          <>
            {/* FTC Compliance: Affiliate Disclosure Badge - Top Right */}
            {isValidUrl && (() => {
              const hasDarkBg =
                isV2 ||
                (() => {
                  const bg = theme.cardBackground || ''
                  return (
                    bg.includes('rgba(0') ||
                    bg.includes('rgb(0') ||
                    bg.includes('#1A1A') ||
                    bg.includes('#000')
                  )
                })()
              const badgeBackground = hasDarkBg
                ? 'rgba(255, 255, 255, 0.25)'
                : 'rgba(0, 0, 0, 0.1)'
              const badgeTextColor = hasDarkBg ? '#FFFFFF' : '#1A1A1A'
              
              return (
                <div 
                  className="absolute top-2 right-2 z-30 px-1.5 py-0.5 rounded text-xs font-medium"
                  style={{
                    background: badgeBackground,
                    color: badgeTextColor,
                    fontSize: '10px',
                    lineHeight: '1.2',
                    fontWeight: 600,
                  }}
                >
                  Ad
                </div>
              )
            })()}
            
            {/* Reasoning Badge removed from mini cards - contextual badge from BundleSection handles this */}
            {/* Reasoning badge would overlap with contextual badge (both at top-left) */}
          </>
        )}
        
        {/* Premium Preview Badge removed - now using inline preview in analysis section */}
        {/* Legacy category hero photography — preserved for easy restore (see PICKS_V2_EXPERIMENT_HIDE_CATEGORY_HERO_PHOTO) */}
        {showCategoryHeroPhoto && heroBackgroundStyle.backgroundImage && (
          <>
            {/* Primary: CSS background-image approach */}
            <div 
              key={`bg-${bundleTitle}-${safeProduct.id}`}
              className="absolute inset-0 rounded-2xl pointer-events-none"
              style={{
                // CRITICAL: Use the backgroundImage directly from heroBackgroundStyle
                backgroundImage: heroBackgroundStyle.backgroundImage,
                backgroundSize: heroBackgroundStyle.backgroundSize || 'cover',
                backgroundPosition: heroBackgroundStyle.backgroundPosition || 'center',
                backgroundRepeat: heroBackgroundStyle.backgroundRepeat || 'no-repeat',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                height: '100%',
                zIndex: 0,
                // CRITICAL: Ensure background is visible
                opacity: 1,
                visibility: 'visible',
                display: 'block',
                // CRITICAL: Ensure background color doesn't interfere
                backgroundColor: 'transparent',
                // Premium cinematic filter: richer contrast, better saturation, subtle vignette
                filter: 'contrast(1.08) saturate(1.12) brightness(0.95)',
                WebkitFilter: 'contrast(1.08) saturate(1.12) brightness(0.95)',
              }}
              data-bundle-title={bundleTitle}
              data-has-background={!!heroBackgroundStyle.backgroundImage}
              data-background-image-url={backgroundImageUrl}
              data-testid={`hero-background-${bundleTitle}`}
              ref={(el) => {
                if (el && bundleTitle === 'toys') {
                  // Use setTimeout to ensure DOM is fully rendered
                  setTimeout(() => {
                    const computedStyle = window.getComputedStyle(el)
                    const bgImage = computedStyle.backgroundImage
                    console.log('[ProductCardGlass] 🧸 Toys background div DOM check:', {
                      elementExists: !!el,
                      computedBackgroundImage: bgImage,
                      hasBackgroundImage: bgImage && bgImage !== 'none' && bgImage !== '',
                      elementStyle: el.style.backgroundImage,
                      parentHeight: el.parentElement?.offsetHeight,
                      elementHeight: el.offsetHeight,
                      elementWidth: el.offsetWidth,
                      parentElement: el.parentElement?.tagName,
                      zIndex: computedStyle.zIndex,
                      position: computedStyle.position,
                      opacity: computedStyle.opacity,
                      visibility: computedStyle.visibility,
                    })
                  }, 100)
                }
              }}
            />
            {/* Fallback: img tag for Toys (in case CSS background-image doesn't work) */}
            {bundleTitle === 'toys' && backgroundImageUrl && (
              <img
                src={backgroundImageUrl}
                alt=""
                className="absolute inset-0 w-full h-full object-cover rounded-2xl pointer-events-none"
                style={{
                  zIndex: 0, // Same level as CSS background div (will show if CSS fails)
                  filter: 'contrast(1.08) saturate(1.12) brightness(0.95)',
                  WebkitFilter: 'contrast(1.08) saturate(1.12) brightness(0.95)',
                }}
                onError={(e) => {
                  console.error('[ProductCardGlass] ❌ Toys background image failed to load:', {
                    bundleTitle,
                    imageUrl: backgroundImageUrl,
                    error: e,
                    target: e.target,
                  })
                  // Try a fallback image if the primary one fails
                  const img = e.target as HTMLImageElement
                  if (img && !img.src.includes('fallback')) {
                    img.src = 'https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=2000&q=95&auto=format&fit=crop'
                    console.log('[ProductCardGlass] 🔄 Trying fallback Toys image')
                  }
                }}
                onLoad={(e) => {
                  const img = e.target as HTMLImageElement
                  console.log('[ProductCardGlass] ✅ Toys background image (img tag) loaded:', {
                    bundleTitle,
                    imageUrl: backgroundImageUrl,
                    naturalWidth: img?.naturalWidth,
                    naturalHeight: img?.naturalHeight,
                  })
                }}
              />
            )}
          </>
        )}
        
        {/* Cinematic overlays — only when category hero photography is active */}
        {showCategoryHeroPhoto && (
          <div 
            className="absolute inset-0 rounded-2xl pointer-events-none"
            style={{
              background: 'linear-gradient(to top, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.05) 50%, transparent 100%)',
              zIndex: 1,
            }}
          />
        )}
        
        {showCategoryHeroPhoto && (
          <div 
            className="absolute inset-0 rounded-2xl pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,0.2) 100%)',
              zIndex: 1,
            }}
          />
        )}
        
        {/* Glassmorphic overlay for hero cards - REMOVED to show background images clearly */}
        {/* CRITICAL: Removed glassmorphic overlay for hero cards to ensure background images are visible */}
        {/* {isHero && (
          <div 
            className="absolute inset-0 rounded-2xl pointer-events-none"
            style={{
              background: theme.cardBackground,
              opacity: 0.05,
              zIndex: 1,
            }}
          />
        )} */}
        
        {/* Content wrapper with relative z-index */}
        {/* For Pack Leader hero cards: Center buttons vertically while keeping title at top */}
        <div className="relative z-10 flex flex-col h-full">
          {/* Hero: category name/icon above image when category imagery is present */}
          {heroTitleAboveImage ? (
            <>
          {/* Product Title - NUCLEAR SCRUB: Final render-time check to eliminate ALL technical slugs */}
          {/* CRITICAL: No subcategory/bundle_title/slug should be rendered here - they're redundant */}
          {(() => {
            // CRITICAL: For Toys bundle, if productTitle is a formatted subcategory (like "Chew Toys"),
            // don't scrub it - it's already been formatted and validated
            const bundleTitleLower = bundleTitle?.toLowerCase() || ''
            const bundleIdLower = bundleId?.toLowerCase() || ''
            // More aggressive detection - check for "toys" anywhere in bundle identifiers
            const isToysBundle = bundleTitleLower === 'toys' || 
                                 bundleIdLower === 'toys' ||
                                 bundleTitleLower.includes('toy') ||
                                 bundleIdLower.includes('toy') ||
                                 bundleIdLower === 'toys' ||
                                 bundleIdLower.includes('toys') ||
                                 (bundleId && String(bundleId).toLowerCase().includes('toy'))
            
            // Check if productTitle is a valid formatted Toys subcategory (e.g., "Chew Toys", "Tennis Balls")
            // CRITICAL: This regex must match ALL possible formatted subcategories from formatSubcategory()
            const isFormattedToysSubcategory = isToysBundle && productTitle && (
              // Match patterns like "Chew Toys", "Fetch Toys", "Puzzle Toys", etc.
              /^(chew|fetch|puzzle|rope|treat|variety|interactive|tennis|plush|training|squeaky|ball|interac)\s+(toys|treats|dispensers|balls|packs)$/i.test(productTitle) ||
              // Match exact known subcategories
              productTitle === 'Treat Dispensers' ||
              productTitle === 'Variety Packs' ||
              productTitle === 'Tennis Balls' ||
              productTitle === 'Chew Toys' ||
              productTitle === 'Fetch Toys' ||
              productTitle === 'Puzzle Toys' ||
              productTitle === 'Rope Toys' ||
              productTitle === 'Plush Toys' ||
              productTitle === 'Interactive Toys' ||
              productTitle === 'Toys' // Allow generic "Toys" for fallback
            )
            
            // NUCLEAR SCRUB: Final render-time slug stripping - last line of defense
            // PRODUCTION CRITICAL: This must catch ALL slugs or production is blocked
            // Use centralized nuclear scrubbing utility
            // BUT: Don't scrub formatted Toys subcategories - they're already valid
            // SKU cards keep catalog product-type hyphens (e.g. No-Pull).
            let finalTitle = productTitle
            if (
              finalTitle &&
              typeof finalTitle === 'string' &&
              !isFormattedToysSubcategory &&
              !isSkuCard
            ) {
              // Apply nuclear scrubbing (but skip if it's a valid formatted Toys subcategory)
              finalTitle = nuclearScrubSlug(finalTitle)
            }
            
            const titleLower = (finalTitle || '').toLowerCase().trim()
            
            // NUCLEAR CHECK: Use centralized utility functions
            // BUT: Don't flag formatted Toys subcategories as slugs
            const isSlug = !isSkuCard && !isFormattedToysSubcategory && isSlugPattern(finalTitle)
            const isBundle = !isSkuCard && !isFormattedToysSubcategory && isBundleName(finalTitle)
            
            // NUCLEAR BLOCK: Check if it's a slug, bundle name, or empty
            // PRODUCTION CRITICAL: Replace slug with AI badge instead of hiding
            // CRITICAL: For Toys bundle, allow "Toys" as fallback even if title is empty/slug
            const isSlugOrBundle = isSlug || isBundle || (!finalTitle || !finalTitle.trim())
            
            // If it's a slug, do NOT show anything - slugs cannot be shown to users
            // CRITICAL: Do NOT show suitabilityReason here - it's already shown in "Wagamour Pick" section below
            // This prevents duplication of the same reasoning text
            // EXCEPTION: For Toys bundle, show "Toys" as fallback if title is empty/slug
            if (isSlugOrBundle) {
              // CRITICAL: For Toys bundle, show "Toys" as fallback instead of hiding completely
              if (isToysBundle && (!finalTitle || !finalTitle.trim() || isSlug || isBundle)) {
                finalTitle = 'Toys'
                // Don't return null - continue to render "Toys" as the title
              } else {
                // NUCLEAR: Never show slugs - return null to hide the title completely
                // Do NOT show formatted subcategory if the original was a slug - it might still contain slug patterns
                return null
              }
            }
            
            // PRODUCTION CRITICAL: Final validation before render
            // Double-check finalTitle doesn't contain any slug patterns using centralized utility
            // NUCLEAR: If it's still a slug after all processing, do NOT render it
            // CRITICAL: Don't flag formatted Toys subcategories as slugs
            const stillHasSlug =
              !isSkuCard &&
              !isFormattedToysSubcategory &&
              (isSlugPattern(finalTitle) || isBundleName(finalTitle))
            
            if (stillHasSlug) {
              // NUCLEAR: Never show slugs to users - return null
              // EXCEPTION: Don't hide if it's a valid formatted Toys subcategory
              if (!isFormattedToysSubcategory) {
                if (process.env.NODE_ENV === 'development') {
                  console.error('[ProductCardGlass] 🚨 PRODUCTION BLOCKER: Slug detected in finalTitle after all scrubbing:', {
                    productId: safeProduct.id,
                    finalTitle,
                    originalTitle: productTitle,
                    isFormattedToysSubcategory,
                  })
                }
                return null
              }
            }
            
            // Additional check: if finalTitle contains hyphenated patterns that look like slugs, hide it
            // CRITICAL: Only hide if it's an exact slug pattern, not if it just contains a hyphen
            // EXCEPTION: Don't hide if it's a valid formatted Toys subcategory
            if (finalTitle && !isFormattedToysSubcategory && /^(toys|walk|health|food|beds)-[a-z0-9-]+$/i.test(finalTitle)) {
              // It's an exact slug pattern - hide it
              if (process.env.NODE_ENV === 'development') {
                console.error('[ProductCardGlass] 🚨 PRODUCTION BLOCKER: Slug pattern detected in finalTitle:', {
                  productId: safeProduct.id,
                  finalTitle,
                  isFormattedToysSubcategory,
                })
              }
              return null
            }
            
            // Final safety check: if title is empty or just whitespace, don't render
            if (!finalTitle || !finalTitle.trim()) {
              return null
            }
            
            // Safe to render - finalTitle has been scrubbed and validated
            const categoryIconSource = [
              safeProduct.category,
              safeProduct.subcategory,
              bundleTitle,
              bundleId,
              finalTitle,
            ]
              .filter(Boolean)
              .join(' ')
            const HeroCategoryIcon =
              isHero && isV2
                ? resolveHeroCategoryIcon(categoryIconSource)
                : null
            const heroIconColor = usesLightHeroText
              ? 'rgba(255, 236, 210, 0.82)'
              : homeV2Text.section

            return (
              <div
                className={`flex items-start gap-2.5 ${isHero ? 'mb-1.5' : 'mb-1.5'}`}
              >
                {HeroCategoryIcon ? (
                  <HeroCategoryIcon
                    className="mt-0.5 h-[18px] w-[18px] shrink-0"
                    style={{ color: heroIconColor, opacity: 0.85 }}
                    aria-hidden
                  />
                ) : null}
                <h3
                  className={`min-w-0 flex-1 ${isHero ? 'text-lg' : 'text-base'} font-semibold ${titleClampClass} overflow-hidden leading-tight ${isHero ? 'drop-shadow-sm' : ''} product-title`}
                  data-product-title={finalTitle}
                  style={{
                    color: usesLightHeroText
                      ? '#FFFFFF'
                      : isV2
                        ? picksV2Type.headline.color
                        : theme.textColor,
                    wordBreak: 'break-word',
                    display: '-webkit-box',
                    WebkitLineClamp: titleLineClamp,
                    WebkitBoxOrient: 'vertical',
                    minHeight: skuTitleBlockMinHeight,
                  }}
                >
                  {finalTitle}
                </h3>
              </div>
            )
          })()}
          

            </>
          ) : null}

          {/* Category imagery (image-first on subcategory cards). */}
          {showCategoryImageryWell ? (
              <CategoryImageryWell
                categoryRepUrl={categoryRepUrl}
                fallbackEmoji={productEmoji}
                isV2={isV2}
                isHero={isHero}
                prioritizeImage={prioritizeImage}
              />
          ) : null}
          
          {/* CRITICAL: No subcategory tag badge - removed to match other bundles' visual approach */}
          {/* Other bundles show subcategory as bold header (product title), not as a tag badge */}
        
          {/* Brand Name + ID Tag - Only show if brand exists and is not Chewy */}
          {/* Hide DEV id chip on category-representative imagery cards (keeps collection preview clean). */}
          {((brandName && brandName.toLowerCase() !== 'chewy') ||
            (process.env.NODE_ENV === 'development' &&
              safeProduct.id &&
              visualRole !== 'category_representative' &&
              visualRole !== 'sku')) ? (
            <div className={`flex items-center justify-between ${showCategoryImageryWell ? 'mb-1' : 'mb-2'}`}>
              {brandName && brandName.toLowerCase() !== 'chewy' && (
                <div 
                  className={`text-xs font-medium ${isHero ? 'drop-shadow-sm' : ''}`}
                  style={{ 
                    color: usesLightHeroText
                      ? 'rgba(255, 255, 255, 0.85)'
                      : isV2
                        ? picksV2Type.metadata.color
                        : theme.textColor,
                    opacity: isHero || isV2 ? 0.85 : 0.7,
                  }}
                >
                  {brandName}
                </div>
              )}
              {/* ID Tag - DEV ONLY: Show need_id (e.g., "cooling_vest", "poop_bags") for debugging */}
              {process.env.NODE_ENV === 'development' &&
                safeProduct.id &&
                visualRole !== 'category_representative' &&
                visualRole !== 'sku' && (() => {
                // Extract need_id from format: rec-{need_id}-{index}
                // Example: "rec-cooling_vest-0" → "cooling_vest"
                const parts = safeProduct.id.split('-')
                const needId = parts.length >= 3 ? parts.slice(1, -1).join('-') : parts[1] || parts[parts.length - 1]
                // Format for display: replace underscores with spaces, capitalize
                const displayId = needId
                  ? needId.replace(/_/g, ' ').split(' ').map(word => 
                      word.charAt(0).toUpperCase() + word.slice(1)
                    ).join(' ').slice(0, 12) // Limit to 12 chars
                  : 'N/A'
                
                return (
                  <div 
                    className="text-xs px-2 py-0.5 rounded-full font-medium opacity-60"
                    style={{
                      background: theme.textColor === '#FFFFFF' 
                        ? 'rgba(255, 255, 255, 0.15)' 
                        : 'rgba(0, 0, 0, 0.1)',
                      color: theme.textColor,
                    }}
                    title={`Product type: ${displayId} (dev only)`}
                  >
                    {displayId}
                  </div>
                )
              })()}
            </div>
          ) : null}
          
{!heroTitleAboveImage ? (
            <>
          {/* Product Title - NUCLEAR SCRUB: Final render-time check to eliminate ALL technical slugs */}
          {/* CRITICAL: No subcategory/bundle_title/slug should be rendered here - they're redundant */}
          {(() => {
            // CRITICAL: For Toys bundle, if productTitle is a formatted subcategory (like "Chew Toys"),
            // don't scrub it - it's already been formatted and validated
            const bundleTitleLower = bundleTitle?.toLowerCase() || ''
            const bundleIdLower = bundleId?.toLowerCase() || ''
            // More aggressive detection - check for "toys" anywhere in bundle identifiers
            const isToysBundle = bundleTitleLower === 'toys' || 
                                 bundleIdLower === 'toys' ||
                                 bundleTitleLower.includes('toy') ||
                                 bundleIdLower.includes('toy') ||
                                 bundleIdLower === 'toys' ||
                                 bundleIdLower.includes('toys') ||
                                 (bundleId && String(bundleId).toLowerCase().includes('toy'))
            
            // Check if productTitle is a valid formatted Toys subcategory (e.g., "Chew Toys", "Tennis Balls")
            // CRITICAL: This regex must match ALL possible formatted subcategories from formatSubcategory()
            const isFormattedToysSubcategory = isToysBundle && productTitle && (
              // Match patterns like "Chew Toys", "Fetch Toys", "Puzzle Toys", etc.
              /^(chew|fetch|puzzle|rope|treat|variety|interactive|tennis|plush|training|squeaky|ball|interac)\s+(toys|treats|dispensers|balls|packs)$/i.test(productTitle) ||
              // Match exact known subcategories
              productTitle === 'Treat Dispensers' ||
              productTitle === 'Variety Packs' ||
              productTitle === 'Tennis Balls' ||
              productTitle === 'Chew Toys' ||
              productTitle === 'Fetch Toys' ||
              productTitle === 'Puzzle Toys' ||
              productTitle === 'Rope Toys' ||
              productTitle === 'Plush Toys' ||
              productTitle === 'Interactive Toys' ||
              productTitle === 'Toys' // Allow generic "Toys" for fallback
            )
            
            // NUCLEAR SCRUB: Final render-time slug stripping - last line of defense
            // PRODUCTION CRITICAL: This must catch ALL slugs or production is blocked
            // Use centralized nuclear scrubbing utility
            // BUT: Don't scrub formatted Toys subcategories - they're already valid
            // SKU cards keep catalog product-type hyphens (e.g. No-Pull).
            let finalTitle = productTitle
            if (
              finalTitle &&
              typeof finalTitle === 'string' &&
              !isFormattedToysSubcategory &&
              !isSkuCard
            ) {
              // Apply nuclear scrubbing (but skip if it's a valid formatted Toys subcategory)
              finalTitle = nuclearScrubSlug(finalTitle)
            }
            
            const titleLower = (finalTitle || '').toLowerCase().trim()
            
            // NUCLEAR CHECK: Use centralized utility functions
            // BUT: Don't flag formatted Toys subcategories as slugs
            const isSlug = !isSkuCard && !isFormattedToysSubcategory && isSlugPattern(finalTitle)
            const isBundle = !isSkuCard && !isFormattedToysSubcategory && isBundleName(finalTitle)
            
            // NUCLEAR BLOCK: Check if it's a slug, bundle name, or empty
            // PRODUCTION CRITICAL: Replace slug with AI badge instead of hiding
            // CRITICAL: For Toys bundle, allow "Toys" as fallback even if title is empty/slug
            const isSlugOrBundle = isSlug || isBundle || (!finalTitle || !finalTitle.trim())
            
            // If it's a slug, do NOT show anything - slugs cannot be shown to users
            // CRITICAL: Do NOT show suitabilityReason here - it's already shown in "Wagamour Pick" section below
            // This prevents duplication of the same reasoning text
            // EXCEPTION: For Toys bundle, show "Toys" as fallback if title is empty/slug
            if (isSlugOrBundle) {
              // CRITICAL: For Toys bundle, show "Toys" as fallback instead of hiding completely
              if (isToysBundle && (!finalTitle || !finalTitle.trim() || isSlug || isBundle)) {
                finalTitle = 'Toys'
                // Don't return null - continue to render "Toys" as the title
              } else {
                // NUCLEAR: Never show slugs - return null to hide the title completely
                // Do NOT show formatted subcategory if the original was a slug - it might still contain slug patterns
                return null
              }
            }
            
            // PRODUCTION CRITICAL: Final validation before render
            // Double-check finalTitle doesn't contain any slug patterns using centralized utility
            // NUCLEAR: If it's still a slug after all processing, do NOT render it
            // CRITICAL: Don't flag formatted Toys subcategories as slugs
            const stillHasSlug =
              !isSkuCard &&
              !isFormattedToysSubcategory &&
              (isSlugPattern(finalTitle) || isBundleName(finalTitle))
            
            if (stillHasSlug) {
              // NUCLEAR: Never show slugs to users - return null
              // EXCEPTION: Don't hide if it's a valid formatted Toys subcategory
              if (!isFormattedToysSubcategory) {
                if (process.env.NODE_ENV === 'development') {
                  console.error('[ProductCardGlass] 🚨 PRODUCTION BLOCKER: Slug detected in finalTitle after all scrubbing:', {
                    productId: safeProduct.id,
                    finalTitle,
                    originalTitle: productTitle,
                    isFormattedToysSubcategory,
                  })
                }
                return null
              }
            }
            
            // Additional check: if finalTitle contains hyphenated patterns that look like slugs, hide it
            // CRITICAL: Only hide if it's an exact slug pattern, not if it just contains a hyphen
            // EXCEPTION: Don't hide if it's a valid formatted Toys subcategory
            if (finalTitle && !isFormattedToysSubcategory && /^(toys|walk|health|food|beds)-[a-z0-9-]+$/i.test(finalTitle)) {
              // It's an exact slug pattern - hide it
              if (process.env.NODE_ENV === 'development') {
                console.error('[ProductCardGlass] 🚨 PRODUCTION BLOCKER: Slug pattern detected in finalTitle:', {
                  productId: safeProduct.id,
                  finalTitle,
                  isFormattedToysSubcategory,
                })
              }
              return null
            }
            
            // Final safety check: if title is empty or just whitespace, don't render
            if (!finalTitle || !finalTitle.trim()) {
              return null
            }
            
            // Safe to render - finalTitle has been scrubbed and validated
            const categoryIconSource = [
              safeProduct.category,
              safeProduct.subcategory,
              bundleTitle,
              bundleId,
              finalTitle,
            ]
              .filter(Boolean)
              .join(' ')
            const HeroCategoryIcon =
              isHero && isV2
                ? resolveHeroCategoryIcon(categoryIconSource)
                : null
            const heroIconColor = usesLightHeroText
              ? 'rgba(255, 236, 210, 0.82)'
              : homeV2Text.section

            return (
              <div
                className={`flex items-start gap-2.5 ${isHero ? 'mb-2' : 'mb-1.5'}`}
              >
                {HeroCategoryIcon ? (
                  <HeroCategoryIcon
                    className="mt-0.5 h-[18px] w-[18px] shrink-0"
                    style={{ color: heroIconColor, opacity: 0.85 }}
                    aria-hidden
                  />
                ) : null}
                <div className="min-w-0 flex-1">
                  {isSkuCard && subcategoryLabel ? (
                    <p
                      className="mb-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]"
                      data-subcategory-label="true"
                      style={
                        isV2
                          ? picksV2Type.eyebrow
                          : {
                              color: theme.textColor,
                              opacity: 0.72,
                            }
                      }
                    >
                      {subcategoryLabel}
                    </p>
                  ) : null}
                  <h3
                    className={`${isHero ? 'text-lg' : 'text-base'} font-semibold ${titleClampClass} overflow-hidden leading-tight ${isHero ? 'drop-shadow-sm' : ''} product-title`}
                    data-product-title={finalTitle}
                    data-catalog-title={isSkuCard ? finalTitle : undefined}
                    style={{
                      color: usesLightHeroText
                        ? '#FFFFFF'
                        : isV2
                          ? picksV2Type.headline.color
                          : theme.textColor,
                      wordBreak: 'break-word',
                      display: '-webkit-box',
                      WebkitLineClamp: titleLineClamp,
                      WebkitBoxOrient: 'vertical',
                      minHeight: skuTitleBlockMinHeight,
                    }}
                  >
                    {finalTitle}
                  </h3>
                </div>
              </div>
            )
          })()}
          

            </>
          ) : null}

          {/* Hero recommendation — Pack Leader agentic intelligence only */}
          {isHero && isPackLeader && (() => {
            const raw =
              safeProduct.suitabilityReason?.trim() ||
              reasoningText?.trim() ||
              ''
            if (!raw) return null
            const snippet = formatHeroRecommendationSnippet(raw)
            if (!snippet) return null
            return (
              <div className="mb-1.5">
                <p
                  className="text-[10px] font-semibold uppercase tracking-[0.12em] mb-0.5"
                  style={isV2 ? picksV2Type.eyebrow : { color: theme.textColor, opacity: 0.72 }}
                >
                  Recommendation
                </p>
                <p
                  className="text-sm leading-snug line-clamp-2"
                  style={
                    isV2
                      ? picksV2Type.body
                      : {
                          color: usesLightHeroText ? 'rgba(255,255,255,0.92)' : theme.textColor,
                          opacity: 0.92,
                        }
                  }
                >
                  {snippet}
                </p>
              </div>
            )
          })()}

          {/* Catalog details stay above commerce (title/subcategory already rendered). */}
          {isSkuEnriched && representativeCatalogName ? (
            <p
              className="mt-1.5 text-sm font-medium leading-snug line-clamp-2"
              data-representative-catalog-name="true"
              data-enrich-catalog-item-id={
                String(
                  (safeProduct as { enrichCatalogItemId?: string })
                    .enrichCatalogItemId || ''
                ) || undefined
              }
              style={
                isV2
                  ? picksV2Type.body
                  : {
                      color: usesLightHeroText
                        ? 'rgba(255,255,255,0.92)'
                        : theme.textColor,
                      opacity: 0.92,
                    }
              }
            >
              {representativeCatalogName}
            </p>
          ) : null}

          {/* Commerce (price + View product) after details; mt-auto aligns CTAs across equal-height cards.
              AI Fit stays a separate sibling below so it does not interleave with commerce. */}
          <div
            className={`mt-auto flex flex-col gap-2 ${showCategoryImageryWell ? 'pt-1.5' : 'pt-2'}`}
            data-card-commerce-footer="true"
            data-sku-commerce="true"
            data-sku-commerce-order="price-then-view-product"
          >
              {verifiedPriceLabel ? (
                <p
                  className="text-sm font-semibold tabular-nums"
                  data-sku-price="true"
                  data-sku-currency={verifiedCurrency || undefined}
                  style={
                    isV2
                      ? picksV2Type.headline
                      : {
                          color: usesLightHeroText ? '#FFFFFF' : theme.textColor,
                        }
                  }
                >
                  {verifiedPriceLabel}
                  {verifiedCurrency ? (
                    <>
                      {' '}
                      <span
                        className="text-[11px] font-medium opacity-80"
                        data-sku-currency-code="true"
                      >
                        {verifiedCurrency}
                      </span>
                    </>
                  ) : null}
                </p>
              ) : null}

              {/* Primary CTA — SKU rails use ctaLabel "View product"; heroes keep Shop-on-category. */}
              <a
                href={isValidUrl ? affiliateUrl : '#'}
                target={isValidUrl ? '_blank' : undefined}
                rel={isValidUrl ? 'sponsored noopener noreferrer' : undefined}
                className="block"
                data-hero-destination={isHero ? affiliateUrl : undefined}
                data-view-product={
                  isSkuCard ||
                  (ctaLabelOverride || '').toLowerCase() === 'view product'
                    ? 'true'
                    : undefined
                }
                onClick={(e) => {
                  if (!isValidUrl) {
                    e.preventDefault()
                    console.warn(
                      '[ProductCardGlass] ⚠️ Blocked click on invalid URL:',
                      affiliateUrl
                    )
                    return
                  }
                  trackEvent('product_click', {
                    page_path:
                      typeof window !== 'undefined' ? window.location.pathname : '',
                    product_id: safeProduct.id,
                    product_name:
                      (productTitle && String(productTitle).trim()) ||
                      safeProduct.title ||
                      safeProduct.name ||
                      '',
                    merchant: affiliatePartnerName,
                  })
                }}
              >
                <div
                  className="w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-center transition-all duration-200 hover:opacity-90"
                  style={
                    isV2
                      ? {
                          background: picksV2CtaPrimary.background,
                          color: picksV2CtaPrimary.color,
                          border: `1px solid ${picksV2CtaPrimary.border}`,
                          boxShadow: picksV2CtaPrimary.boxShadow,
                        }
                      : {
                          background:
                            theme.textColor === '#FFFFFF' ? '#FFFFFF' : '#1A1A1A',
                          color:
                            theme.textColor === '#FFFFFF' ? '#1A1A1A' : '#FFFFFF',
                        }
                  }
                >
                  {ctaLabelOverride || `Shop on ${affiliatePartnerName}`}
                </div>
              </a>
              {hasViewProduct ? (
                <a
                  href={viewProductUrl}
                  target="_blank"
                  rel="sponsored noopener noreferrer"
                  className="block"
                  data-view-product="true"
                  data-view-product-url={viewProductUrl}
                >
                  <div
                    className="w-full py-2 px-4 rounded-xl text-sm font-semibold text-center transition-all duration-200 hover:opacity-90"
                    style={
                      isV2
                        ? {
                            background: 'rgba(255, 248, 240, 0.14)',
                            color: picksV2Type.headline.color,
                            border: '1px solid rgba(255, 236, 210, 0.34)',
                          }
                        : {
                            background: 'transparent',
                            color: theme.textColor,
                            border: `1px solid ${theme.cardBorder}`,
                          }
                    }
                  >
                    {viewProductCta}
                  </div>
                </a>
              ) : null}
              {hasSubcategoryBrowse ? (
                <a
                  href={subcategoryBrowseUrl}
                  target="_blank"
                  rel="sponsored noopener noreferrer"
                  className="block"
                  data-subcategory-browse="true"
                  data-subcategory-browse-url={subcategoryBrowseUrl}
                >
                  <div
                    className="w-full py-2 px-4 rounded-xl text-sm font-semibold text-center transition-all duration-200 hover:opacity-90"
                    style={
                      isV2
                        ? {
                            background: 'rgba(255, 248, 240, 0.10)',
                            color: picksV2Type.headline.color,
                            border: '1px solid rgba(255, 236, 210, 0.28)',
                          }
                        : {
                            background: 'transparent',
                            color: theme.textColor,
                            border: `1px solid ${theme.cardBorder}`,
                          }
                    }
                  >
                    {subcategoryBrowseCta}
                  </div>
                </a>
              ) : null}
          </div>

          {/* AI Fit below commerce — only when compact block can render (hero / Pack Leader / SKU). */}
          {isHero || isPackLeader || isSkuCard ? (
            <div
              className="mt-3 border-t pt-3"
              data-ai-fit-section="true"
              style={{
                borderColor: isV2
                  ? 'rgba(255, 236, 210, 0.22)'
                  : theme.textColor === '#FFFFFF'
                    ? 'rgba(255,255,255,0.18)'
                    : 'rgba(0,0,0,0.08)',
              }}
            >
              <AIFitBlock
                variant="compact"
                isPackLeader={isPackLeader}
                isHero={isHero}
                isSkuCard={isSkuCard}
                dogName={dogProfile?.name}
                matchScore={product.matchScore}
                suitabilityReason={safeProduct.suitabilityReason || null}
                analysisUnavailable={
                  // SKU cards: only trust explicit server flag — missing reason is not
                  // "unavailable marketing"; Pack Leader fetches on demand instead.
                  isSkuCard
                    ? Boolean(
                        (safeProduct as { analysisUnavailable?: boolean })
                          .analysisUnavailable
                      )
                    : Boolean(
                        (safeProduct as { analysisUnavailable?: boolean })
                          .analysisUnavailable
                      ) ||
                      !(
                        safeProduct.suitabilityReason &&
                        String(safeProduct.suitabilityReason).trim()
                      )
                }
                productId={String(safeProduct.id || '') || undefined}
                productTitle={
                  (productTitle && String(productTitle).trim()) ||
                  safeProduct.title ||
                  safeProduct.name ||
                  undefined
                }
                affiliateUrl={isValidUrl ? affiliateUrl : undefined}
                provider={
                  String(
                    safeProduct.provider ||
                      safeProduct.merchant ||
                      'chewy'
                  ) || undefined
                }
                requestDogId={
                  (dogProfile as { id?: string } | null | undefined)?.id || null
                }
                category={safeProduct.category || null}
                subcategory={safeProduct.subcategory || null}
                bundleTitle={bundleTitle}
                dogProfile={dogProfile}
                theme={theme}
                className="!mt-0"
              />
            </div>
          ) : null}
        </div>
      </div>
    </>
  )
}

